'use client'

import * as React from 'react'
import { useUIStore } from './store'
import type { UiLanguage } from './types'

interface Messages {
  [key: string]: string | Messages
}

const messages: Record<UiLanguage, Messages> = {
  cs: {
    header: {
      likedFlats: 'Oblíbené byty',
      planner: 'Plánovač',
      commandSearch: 'Hledat',
      savedSearches: 'Uložená hledání',
      searches: 'Hledání',
      explore: 'Prozkoumat',
      liked: 'Oblíbené',
      myListings: 'Moje inzeráty',
      post: 'Inzerovat',
      postTip: 'Vložte inzerát — asistent ho sestaví s vámi',
      myListingsTip: 'Moje inzeráty — vkládání i správa na jednom místě',
    },
    regionalSettings: {
      title: 'Regionální nastavení',
      language: 'Jazyk',
    },
    footer: {
      about: 'O nás',
      privacy: 'Soukromí',
      terms: 'Podmínky',
      twitter: 'Twitter',
      email: 'Email',
    },
    home: {
      title: 'Najděte si ideální byt',
      subtitle: 'Hledejte byty stejně jednoduše, jako byste psali kamarádovi. Přirozený jazyk, chytré filtry, okamžité výsledky.',
      termsIntro: 'Použitím vyhledávání souhlasíte s našimi',
      termsLink: 'Podmínkami služby',
    },
    palette: {
      placeholder: 'Hledejte byty nebo napište příkaz…',
      noResults: 'Nic nenalezeno.',
      searchGroup: 'Hledání',
      searchFor: 'Najít byty:',
      recent: 'Poslední hledání',
      savedGroup: 'Hledání a upozornění',
      new: 'nových',
      saveSearch: 'Uložit toto hledání',
      openSaved: 'Otevřít Hledání a upozornění',
      thisSearch: 'Toto hledání',
      clearFilters: 'Zrušit všechny filtry',
      active: 'aktivní',
      toMap: 'Přepnout na mapu',
      toList: 'Přepnout na seznam',
      toSale: 'Přepnout na byty na prodej',
      toRent: 'Přepnout na byty k pronájmu',
      expandAll: 'Rozbalit všechny popisy',
      collapseAll: 'Sbalit všechny popisy',
      showHidden: 'Zobrazit skryté inzeráty',
      copyLink: 'Kopírovat odkaz na hledání',
      copied: 'Odkaz na hledání zkopírován',
      copyFailed: 'Odkaz se nepodařilo zkopírovat',
      shortcuts: 'Procházet výsledky klávesnicí',
      sortGroup: 'Řazení výsledků',
      goTo: 'Přejít na',
      gotoSearch: 'Hledání',
      lastSearch: 'Zpět na poslední hledání',
      gotoLiked: 'Oblíbené byty',
      gotoSearches: 'Moje hledání',
      gotoNotifications: 'Upozornění',
      gotoMyListings: 'Moje inzeráty',
      gotoPost: 'Přidat inzerát',
      gotoSettings: 'Nastavení',
      gotoPricing: 'Ceník',
      gotoAbout: 'O nás',
      language: 'Jazyk',
      appearance: 'Vzhled',
      switchTo: 'Přepnout na',
      mode: 'režim',
      theme: {
        light: 'světlý',
        dark: 'tmavý',
        hacker: 'hacker',
        system: 'systémový',
      },
      current: 'aktuální',
      helpGroup: 'Nápověda a účet',
      tour: 'Jak Roostr funguje',
      signIn: 'Přihlásit se nebo vytvořit účet',
      signOut: 'Odhlásit se',
      navigate: 'Pohyb',
      select: 'Vybrat',
      close: 'Zavřít',
    },
    gate: {
      hook: 'Okamžitá upozornění',
      headline1: 'Buďte první',
      headline2: 've dveřích.',
      intro1: 'Nejlepší byty v Česku zmizí ',
      introStrong: 'během pár hodin',
      intro2: '. Roostr vám dá vědět v okamžiku, kdy se objeví byt podle vašich představ — najde ho AI, která za vás čte každý inzerát.',
      ctaLead: 'Uložte si hledání a dostávejte okamžitá upozornění',
      google: 'Pokračovat přes Google',
      create: 'Vytvořit účet',
      already: 'Už jste s námi?',
      signIn: 'Přihlásit se',
      chips: {
        rooms2: '2+ pokoje',
        under25k: 'do 25 tisíc',
        balcony: 'balkon',
        terrace: 'terasa',
        furnished: 'zařízený',
        dishwasher: 'myčka',
        washer: 'pračka',
        ac: 'klimatizace',
        elevator: 'výtah',
        bathtub: 'vana',
        parking: 'parkování',
        pets: 'se zvířaty',
      },
      peek: {
        eyebrow: 'Nahlédněte dovnitř',
        homes: ' volných bytů, ',
        one: 'jeden',
        yours: ' je váš.',
        sub: 'Dnes čerstvé z celého Česka. Přihlaste se a uložte si svůj výběr.',
      },
      unlock: 'Odemkněte dalších {n}',
      thousands: 'tisíce',
      story: {
        eyebrow: 'Proč nájemníci milují Roostr',
        title1: 'Vše, ',
        title2: 'pracuje pro vás.',
      },
      features: {
        alerts: {
          eyebrow: 'Okamžitá upozornění',
          title: 'V okamžiku, kdy se objeví shoda',
          body: 'Nové byty mizí rychle. Uložte si hledání jednou a Roostr hlídá trh 24/7 — ozve se v sekundě, kdy se něco hodí, dřív než se ostatní stihnou podívat.',
        },
        ai: {
          eyebrow: 'AI vyhledávání',
          title: 'Hledejte, jako když píšete kamarádovi',
          body: '„2+kk s balkonem v Holešovicích, do 25 tisíc včetně energií.“ Roostr rozumí ceně, dispozici, lokalitě i vybavení najednou — žádné zdlouhavé filtry.',
        },
        swipe: {
          eyebrow: 'Swipem protřídit',
          title: 'Uvidíte jen to, co opravdu sedí',
          body: 'Nové shody přichází jako přehledný balíček. Swipe doprava = nechat, doleva = přeskočit — minuta denně a užší výběr se staví sám.',
        },
        breakdown: {
          eyebrow: 'Rozpad ceny',
          title: 'Skutečné měsíční náklady, položka po položce',
          body: 'Žádná překvapení při podpisu. Roostr přečte popis každého inzerátu a vytáhne nájem, energie i kauci — skutečnou celkovou cenu uvidíte bez louskání drobného písma.',
        },
      },
      why: {
        title: 'Proč si založit účet?',
        body1: 'Hledat můžete i bez přihlášení. Účet ale ',
        strong1: 'pracuje za vás',
        body2: ' — uloží vaše hledání a Roostr hlídá trh 24/7, takže vám ',
        strong2: 'okamžitě dá vědět',
        body3: ', jakmile se objeví nový byt podle vašich představ. Váš výběr navíc máte synchronizovaný napříč všemi zařízeními.',
        chip1: 'Uložená hledání',
        chip2: 'Okamžitá upozornění',
        chip3: 'Synchronizace zařízení',
      },
      footer: 'AI bez botů · Žádný spam · Vyrobeno v Praze',
      visual: {
        alertTitle: 'Nová shoda — Ten tichý',
        alertBody: 'Světlý 2+kk u Riegrových sadů · 23 500 Kč',
        now: 'teď',
        swipeTitle: 'Útulný 2+kk, Žižkov',
        baseRent: 'Základní nájem',
        utilities: 'Energie a služby',
        totalMonth: 'Celkem / měsíc',
        depositNote: '+ 47 000 Kč kauce (2 měsíce), jednorázově',
      },
      card: {
        monthlyRent: 'Měsíční nájem',
        utilities: 'Energie',
        deposit: 'Kauce (2 měsíce)',
        perMonth: '/měs',
        perM2: 'ZA M²',
        totalFirstMonth: 'První měsíc celkem',
        min: 'min',
        transit: 'MHD',
        mustHaves: 'MUSÍ MÍT',
        features: 'VYBAVENÍ',
      },
    },
    settings: {
      back: 'Zpět',
      title: 'Nastavení',
      profile: 'Profil',
      appearance: 'Vzhled',
      searchDefaults: 'Výchozí hledání',
      language: 'Jazyk',
      notifications: 'Notifikace',
      subscription: 'Předplatné',
      privacyAndData: 'Soukromí a data',
      profileHeading: 'Profil',
      profileDescription: 'Spravujte informace o svém účtu.',
      photoSyncedFromGoogle: 'Fotka synchronizovaná z Google účtu',
      displayName: 'Zobrazované jméno',
      yourName: 'Vaše jméno',
      saved: 'Uloženo!',
      saving: 'Ukládání...',
      save: 'Uložit',
      email: 'E-mail',
      accountEmail: 'Účetní e-mail',
      fromGoogle: 'Z Google',
      password: 'Heslo',
      resetLinkSent: 'Odkaz byl odeslán!',
      changePassword: 'Změnit heslo',
      resetEmailSentTo: 'Poslali jsme odkaz pro reset na',
      resetEmailHint: 'Pošleme vám odkaz pro reset hesla na e-mail.',
      appearanceHeading: 'Vzhled',
      appearanceDescription: 'Přizpůsobte si, jak Roostr vypadá a funguje.',
      theme: 'Motiv',
      light: 'Světlý',
      dark: 'Tmavý',
      hacker: 'Hacker',
      system: 'Systém',
      autoDetect: 'Automaticky',
      defaultSearchView: 'Výchozí zobrazení hledání',
      defaultSearchViewHint: 'Které zobrazení se má otevřít při načtení výsledků.',
      listingCardDensity: 'Hustota karet inzerátů',
      listingCardDensityHint: 'Kompaktní režim zobrazuje menší karty, takže uvidíte více výsledků najednou.',
      comfortable: 'Pohodlné',
      compact: 'Kompaktní',
      largerCards: 'Větší karty',
      moreResults: 'Více výsledků',
      websiteLanguage: 'Jazyk webu',
      websiteLanguageHint: 'Jazyk rozhraní pro tlačítka, popisky a menu.',
      languageTitle: 'Jazyk',
      languageCardHint: 'Jazyk rozhraní a jazyk, ve kterém odpovídají funkce s AI.',
      aiSummaryLanguage: 'Jazyk AI shrnutí',
      aiSummaryLanguageHint: 'AI shrnutí popisů inzerátů se píše v tomto jazyce.',
      secLanguage: 'Jazyk',
      searchDefaultsHeading: 'Výchozí hledání',
      searchDefaultsDescription: 'Nastavte výchozí hodnoty pro každé nové hledání.',
      defaultCity: 'Výchozí město',
      defaultCityHint: 'Předvybrané město při novém hledání.',
      defaultSortOrder: 'Výchozí řazení',
      defaultSortOrderHint: 'Jak se mají výsledky standardně řadit.',
      showEstimatedTotal: 'Zobrazovat odhad celkové ceny',
      showEstimatedTotalHint: 'Po zapnutí cena zahrnuje i odhad energií a poplatků.',
      listingFreshness: 'Čerstvost inzerátů',
      listingFreshnessHint: 'Zobrazí jen novější inzeráty podle vybraného období.',
      dealbreakerFilters: 'Dealbreaker filtry',
      dealbreakerFiltersHint: 'Trvalé požadavky pro všechna hledání.',
      mustHave: 'Musí obsahovat',
      mustHaveHint: 'Inzeráty musí obsahovat všechny tyto vlastnosti.',
      searchFeaturesToAdd: 'Hledat vlastnosti k přidání...',
      sortRelevance: 'Relevance',
      sortNewest: 'Nejnovější',
      sortOldest: 'Nejstarší',
      sortPriceAsc: 'Cena: od nejnižší',
      sortPriceDesc: 'Cena: od nejvyšší',
      sortAreaAsc: 'Plocha: od nejmenší',
      sortAreaDesc: 'Plocha: od největší',
      sortRoomsAsc: 'Pokoje: od nejméně',
      sortRoomsDesc: 'Pokoje: od nejvíce',
      freshness7: 'Posledních 7 dní',
      freshness14: 'Posledních 14 dní',
      freshness30: 'Posledních 30 dní',
      freshnessAny: 'Libovolné stáří',
      notificationsHeading: 'Notifikace',
      notificationsDescription: 'Vyberte, jak chcete dostávat upozornění na nové inzeráty a změny ceny.',
      notificationChannels: 'Kanály notifikací',
      emailNotifications: 'E-mailové notifikace',
      emailNotificationsHint: 'Dostávejte aktualizace uložených hledání e-mailem.',
      pushNotifications: 'Push notifikace',
      pushNotificationsHint: 'Push upozornění v prohlížeči pro okamžité novinky.',
      whatsappNotifications: 'WhatsApp',
      whatsappNotificationsHint: 'Upozornění na nové inzeráty přímo na WhatsApp.',
      comingSoon: 'Brzy',
      frequency: 'Frekvence',
      frequencyHint: 'Jak často vám máme posílat upozornění?',
      frequencyInstant: 'Okamžitě',
      frequencyInstantHint: 'Jakmile se objeví',
      frequencyDaily: 'Denní souhrn',
      frequencyDailyHint: 'Jednou denně',
      frequencyWeekly: 'Týdenní souhrn',
      frequencyWeeklyHint: 'Jednou týdně',
      frequencyPerSearchHint: 'Každé uložené hledání má vlastní frekvenci upozornění.',
      frequencyPerSearchLink: 'Spravovat u uložených hledání',
      // Roostr card-stack settings page (web/settings.jsx)
      profileHint: 'Předvyplní kontaktní krok při vkládání inzerátu.',
      phone: 'Telefon',
      phoneHint: 'Volitelné — zobrazí se jen tam, kde to povolíte',
      notificationsHint: 'Jak vás zastihnou nové shody z uložených hledání.',
      manageOnSearches: 'Uložená hledání',
      secProfile: 'Profil',
      secNotifications: 'Oznámení',
      secAppearance: 'Vzhled',
      secPreferences: 'Hledání a preference',
      secSubscription: 'Předplatné',
      secAccount: 'Účet',
      trackedDestinations: 'Sledované cíle',
      trackedDestinationsHint: 'U každého inzerátu se zobrazí dojezd k těmto místům. Až 3.',
      addDestination: 'Přidat cíl',
      destinationLabel: 'Popis (Posilovna, Rodiče…)',
      destinationLabelShort: 'Popis',
      destinationAddress: 'Adresa',
      destinationsFull: '3 ze 3 — jeden odeberte, ať můžete sledovat další.',
      removeDestination: 'Odebrat cíl',
      add: 'Přidat',
      mustHaves: 'Nezbytnosti',
      mustHavesHint: 'Domovy se podle nich hodnotí všude. Až 5.',
      mustHavesFull: 'Pět je maximum — nejdřív jeden odeberte',
      mustHavesSearch: 'Hledat všechny vlastnosti…',
      noFeaturesMatch: 'Žádné vlastnosti neodpovídají hledání.',
      chooseMustHaves: 'Vybrat požadavky',
      editMustHaves: 'Přidat nebo upravit',
      done: 'Hotovo',
      selectedSuffix: 'vybráno',
      loadingFeatures: 'Načítání vlastností…',
      account: 'Účet',
      signOut: 'Odhlásit se',
      deleteAccountSub: 'Odstraní váš profil, inzeráty a uložená hledání',
      deleteEllipsis: 'Smazat…',
      confirmDelete: 'Ano, smazat',
      deleteFailed: 'Účet se nepodařilo smazat. Zkuste to prosím znovu.',
      quietHours: 'Tiché hodiny',
      quietHoursHint: 'Pozastaví notifikace během vybraného času.',
      from: 'Od',
      until: 'Do',
      to: 'do',
      privacyHeading: 'Soukromí a data',
      privacyDescription: 'Spravujte svá data, historii hledání a nastavení soukromí.',
      searchHistory: 'Historie hledání',
      searchHistoryHint: 'Ukládejte nedávná hledání pro rychlý přístup.',
      cleared: 'Vymazáno!',
      clearing: 'Mažu...',
      clearSearchHistory: 'Vymazat celou historii hledání',
      clearAction: 'Vymazat',
      exportData: 'Exportovat data',
      exportDataHint: 'Stáhněte všechna data jako JSON soubor.',
      exportAction: 'Stáhnout',
      downloaded: 'Staženo!',
      exporting: 'Exportuji...',
      exportAsJson: 'Exportovat data jako JSON',
      deleteAccount: 'Smazat účet',
      deleteAccountHint: 'Trvale smaže účet i všechna data. Nelze vrátit zpět.',
      deleteMyAccount: 'Smazat můj účet',
      deleting: 'Mažu...',
      confirmDeleteAll: 'Ano, smazat vše',
      cancel: 'Zrušit',
      subscriptionHeading: 'Předplatné',
      subscriptionDescription: 'Spravujte svůj plán Roostru.',
      welcomePenthouse: 'Vítejte v Penthouse! Užijte si všechny prémiové funkce.',
      active: 'Aktivní',
      activating: 'Aktivace...',
      currentPlan: 'Aktuální plán',
      freePlan: 'Roostr (zdarma)',
      cancelsOn: 'Končí',
      renewsOn: 'Obnoví se',
      manageSubscription: 'Spravovat předplatné',
      redirecting: 'Přesměrování...',
      activatingMessage: 'Platba přijata - aktivujeme vaše předplatné...',
      delayedActivation: 'Aktivace trvá déle než obvykle. Zkuste stránku obnovit.',
      refreshPage: 'Obnovit stránku',
      upgradeHint: 'Přejděte na Penthouse a odemkněte všechny funkce.',
      premiumFeatures: 'Prémiové funkce',
      upgradeToPenthouse: 'Přejít na Penthouse',
      viewPricing: 'Zobrazit ceník',
      openPortalFailed: 'Nepodařilo se otevřít portál předplatného.',
      startCheckoutFailed: 'Nepodařilo se spustit platbu.',
      stripeNote: 'Platby bezpečně zpracovává Stripe. Platební údaje neukládáme.',
      appleManageNote: 'Účtováno přes Apple. Předplatné spravujete nebo zrušíte na iPhonu v App Store (Nastavení → vaše jméno → Předplatná).',
      appleBillingNote: 'Platby zpracovává Apple prostřednictvím App Store. Platební údaje neukládáme.',
    },
    kbd: {
      nextPrev: 'další / předchozí',
      photos: 'fotky',
      summary: 'souhrn',
      like: 'oblíbit',
      allKeys: 'všechny klávesy',
      title: 'Klávesové zkratky',
      subtitle: 'Procházejte výsledky bez myši',
      move: 'Pohyb',
      actOn: 'Akce na vybrané kartě',
      startBrowsing: 'Začít procházet',
      nextListing: 'Další inzerát',
      nextPrevLong: 'Další / předchozí',
      photosLong: 'Fotky',
      stop: 'Ukončit procházení',
      summaryLong: 'AI souhrn',
      likeLong: 'Oblíbit',
      hideLong: 'Skrýt',
      open: 'Otevřít detail',
      openNewTab: 'Otevřít v nové kartě',
    },
    userMenu: {
      signIn: 'Přihlásit se',
      searchesAgents: 'Hledání a agenti',
      savedSearches: 'Uložená hledání',
      myListings: 'Moje inzeráty',
      likedListings: 'Oblíbené inzeráty',
      commandPalette: 'Příkazová paleta',
      settings: 'Nastavení',
      signOut: 'Odhlásit se',
    },
    search: {
      list: 'Seznam',
      map: 'Mapa',
      quickRooms: 'Pokoje',
      quickRoomsUnit: 'pokoje',
      quickPrice: 'Cena',
      quickDistrict: 'Čtvrť',
      chipGroupRooms: 'Pokoje',
      chipGroupPrice: 'Cena',
      chipGroupFeatures: 'Vybavení',
      chips: {
        rooms1: '1 pokoj',
        rooms2: '2 pokoje',
        rooms3: '3 pokoje',
        rooms4plus: '4+ pokoje',
        under15k: 'do 15 tisíc',
        under20k: 'do 20 tisíc',
        under30k: 'do 30 tisíc',
      },
      voiceStart: 'Hledat hlasem',
      voiceStop: 'Zastavit diktování',
      viewAsList: 'Zobrazit výsledky jako seznam',
      viewOnMap: 'Zobrazit výsledky na mapě',
      sortBy: 'Řadit:',
      changeSort: 'Změnit řazení',
      filters: 'Filtry',
      searchApartmentsPlaceholder: 'Hledat byty...',
      areas: 'oblasti',
      apartment: 'byt',
      room: 'pokoj',
      house: 'dům',
      rent: 'Pronájem',
      sale: 'Prodej',
      searchButton: 'Hledat',
      examplePlaceholder: '2 pokoje, Praha 7, do 20k...',
      typeLabel: 'Typ:',
      transactionLabel: 'Transakce:',
      cityLabel: 'Město:',
      select: 'Vybrat',
      any: 'Libovolné',
      districtLabel: 'Čtvrť:',
      district: 'Čtvrť',
      districtAll: 'Vše',
      searchCities: 'Hledat města...',
      noCitiesFound: 'Žádná města',
      searchDistricts: 'Hledat čtvrti...',
      typeToSearchDistricts: 'Začněte psát název čtvrti...',
      noDistrictsFound: 'Žádné čtvrti',
      clearAll: 'Vymazat vše',
      recentSearches: 'Poslední hledání',
      matchingSearches: 'Odpovídající hledání',
      noMatchingSearches: 'Žádná odpovídající hledání',
      hideExamples: 'Skrýt příklady',
      moreExamples: 'Více příkladů',
      howItWorks: 'Jak to funguje',
      features: 'Vybavení',
      allFilters: 'Všechny filtry',
      clearFilters: 'Vymazat filtry',
      rooms: 'Pokoje',
      price: 'Cena',
      area: 'Plocha',
      estTotalTooltip: 'Odhad celkové ceny zahrnuje nájem, energie, poplatky, parkování a internet — AI jej odhaduje z popisu inzerátu. U inzerátů bez těchto údajů se použije základní nájem.',
      upTo: 'do',
      errorTitle: 'Chyba vyhledávání',
      dismissError: 'Zavřít chybu',
      searching: 'Vyhledávám...',
      apartmentsFound: 'bytů nalezeno',
      resultsFor: 'Výsledky pro:',
      semanticHint: 'Sémantické hledání: výsledky podle významu',
      removeSemantic: 'Odebrat sémantický dotaz',
      removeFilter: 'Odebrat',
      filter: 'filtr',
      mustHaveTitle: 'Povinný filtr (musí obsahovat) — klikněte pro úpravu v Nastavení',
      excludedTitle: 'Vyloučený filtr — klikněte pro úpravu v Nastavení',
      freshnessTitle: 'Filtr čerstvosti — klikněte pro úpravu v Nastavení',
      lastDays: 'Posledních',
      days: 'dní',
      saveThisSearch: 'Uložit toto hledání',
      signInToSaveSearches: 'Přihlaste se pro ukládání hledání',
      saveSearch: 'Uložit hledání',
      saveSearchTitle: 'Uložit hledání',
      saveSearchDescription: 'Uložte aktuální kritéria a rychle se k nim vraťte.',
      searchName: 'Název hledání',
      searchNamePlaceholder: 'Zadejte název hledání',
      searchCriteria: 'Kritéria hledání',
      deployAgentsHint: 'Po uložení nasaďte agenty v sekci',
      searchesAndAgents: 'Hledání a agenti',
      cancel: 'Zrušit',
      saving: 'Ukládám...',
      turnOnNotifications: 'Zapnout notifikace',
      notificationsHint: 'Upozorní vás na nové byty odpovídající tomuto hledání.',
      alertsLabel: 'Upozornění',
      cadenceOffHint: 'Bez upozornění — nové shody najdete v sekci Co je nového.',
      signInToSave: 'Pro ukládání hledání se přihlaste',
      enterSearchName: 'Zadejte název hledání',
      failedToSave: 'Uložení se nepodařilo. Zkuste to znovu.',
      searchLimitReached: 'Dosáhli jste limitu {n} uložených hledání — smažte jedno a přidejte nové.',
      manageSearches: 'Spravovat hledání',
      queryLabel: 'Dotaz',
      filtersLabel: 'Filtry',
      travelLabel: 'Dojezd',
      sortLabel: 'Řazení',
      cardSize: 'Velikost karet',
      of: 'z',
      previousPage: 'Předchozí stránka',
      nextPage: 'Další stránka',
      noResultsToShow: 'Žádné výsledky',
      load: 'Načíst',
      signInToContinue: 'Pro pokračování ve vyhledávání se přihlaste.',
      genericError: 'Něco se pokazilo. Zkuste to znovu.',
      connectionError: 'Nelze se připojit k vyhledávání. Zkontrolujte připojení a zkuste to znovu.',
      // Roostr Web redesign — toolbar, insights strip, map view, save-search dialog (Phase 7.1)
      grid: 'Seznam',
      hidden: 'skryto',
      hiddenListings: 'Skryté inzeráty',
      hiddenListing: 'Skrytý inzerát',
      clickToUnhide: 'Kliknutím zobrazíte zpět',
      unhideAll: 'Zobrazit vše zpět',
      sortedBy: 'seřazeno dle',
      page: 'Strana',
      pageAbbr: 'str.',
      close: 'Zavřít',
      jumpToDistrict: 'Přejít na čtvrť…',
      jumpByPrice: 'Skok dle ceny',
      jumpByDate: 'Skok dle data',
      jumpByArea: 'Skok dle plochy',
      jumpByRooms: 'Skok dle dispozice',
      whatItWatches: 'Co hlídá',
      allOfCity: 'Celá Praha',
      matchesRightNow: 'shod právě teď',
      mapUnavailableTitle: 'Mapa si dává pauzu',
      mapUnavailableText: 'Mapová služba je momentálně nedostupná — projděte si výsledky v seznamu.',
      backToGrid: 'Zpět na seznam',
      onMap: 'na mapě',
      unmappable: '{n} bez polohy',
      unmappableHint: '{n} odpovídajících inzerátů nemá polohu, proto je nelze zobrazit na mapě',
      inView: 'V zobrazení: {n}',
      inViewCapped: 'V zobrazení: {total} · zobrazeno prvních {shown}',
      inViewFollows: 'sleduje mapu',
      districtBack: 'Zpět na mapu',
      districtShowMore: 'Zobrazit další ({shown} z {total})',
      panelOpen: 'Zobrazit seznam',
      panelClose: 'Skrýt seznam — klávesa [',
      panelResize: 'Změnit šířku seznamu',
      inViewHint: 'Seznam sleduje mapu — posuňte ji nebo přibližte a změní se, co je zde vidět.',
      noResultsInView: 'V této části mapy nejsou žádné inzeráty',
      noResultsInViewHint: 'Oddalte nebo posuňte mapu, uvidíte víc.',
      lensMustHave: 'Povinné',
      lensMustHaveHint: 'kolik vašich povinných požadavků každý byt splňuje',
      lensHas: 'Má',
      lensNoMustHaves: 'Žádný z vašich povinných požadavků',
      lensMissing: 'chybí',
      lensPriceHint: 'měsíční nájem · tisíce Kč',
      lensAreaHint: 'velikost bytu',
      lensRooms: 'Pokoje',
      lensRoomsHint: 'pokoje + kuchyňský kout',
      districtHoverHint: 'Najetím myší zvýrazníte čtvrť na mapě',
      home: 'byt',
      homeFew: 'byty',
      homes: 'bytů',
      medianRentIn: 'Medián nájmu –',
      medianShort: 'med',
      km: 'km',
      centre: 'centrum',
      kmToCentreTitle: 'Vzdálenost vzdušnou čarou do centra města',
      perMonth: '/měs.',
      like: 'Přidat do oblíbených',
      unlike: 'Odebrat z oblíbených',
      details: 'Detail',
    },
    filterModal: {
      searchFilters: 'Hledat filtry…',
      locationType: 'Lokalita a typ',
      districts: 'Čtvrti',
      selectedCount: '({n} vybráno)',
      searchDistrictsIn: 'Hledat čtvrti v {city}…',
      removeDistrict: 'Odebrat čtvrť',
      filterBy: 'Filtrovat podle',
      rentOnly: 'Jen nájem',
      inclUtilities: 'Vč. energií',
      roomsHintEmpty: 'Klepněte na číslo — druhým klepnutím vytvoříte rozmezí.',
      roomsHintExact: 'Přesně {rooms} — klepnutím na další číslo vytvoříte rozmezí.',
      roomsHintRange: '{lo} až {rooms}.',
      roomOne: 'pokoj',
      roomFew: 'pokoje',
      roomMany: 'pokojů',
      loadingFilters: 'Načítám filtry…',
      noFiltersFound: 'Žádné filtry nenalezeny',
      tryDifferentTerm: 'Zkuste jiný hledaný výraz.',
      showHomesOne: 'Zobrazit {n} byt',
      showHomesFew: 'Zobrazit {n} byty',
      showHomesMany: 'Zobrazit {n} bytů',
      showResults: 'Zobrazit výsledky',
      close: 'Zavřít',
      minValue: 'Minimum',
      maxValue: 'Maximum',
      minShort: 'Min',
      maxShort: 'Max',
    },
    liked: {
      title: 'Oblíbené inzeráty',
      signInMessage: 'Přihlaste se a zobrazte své oblíbené inzeráty.',
      signIn: 'Přihlásit se',
      loadError: 'Nepodařilo se načíst oblíbené inzeráty',
      tryAgain: 'Zkusit znovu',
      emptyTitle: 'Zatím žádné oblíbené inzeráty',
      emptyText: 'Klikněte na srdce u inzerátu a zobrazí se tady.',
      browseListings: 'Procházet inzeráty',
      countOne: 'inzerát',
      countMany: 'inzerátů',
      emptyHint: 'Vaše oblíbené byty se zobrazí tady.',
      // Roostr Web redesign — collaborative shortlist (Phase 7.1)
      headerTitle: 'Oblíbené',
      homeOne: 'byt',
      homeFew: 'byty',
      homeMany: 'bytů',
      subtitle: 'Očíslované, ať se o nich snadno mluví — „co třeba #3?“ prostě funguje.',
      shareShortlist: 'Sdílet užší výběr',
      removedToast: 'Odebráno z užšího výběru',
      removeFromShortlist: 'Odebrat z užšího výběru',
      openDetails: 'Otevřít detail inzerátu',
      signInTitle: 'Přihlaste se a uvidíte své oblíbené byty',
      sort: 'Řadit',
      sortNewest: 'Od nejnovějších',
      sortPriceAsc: 'Cena: od nejnižší',
      sortPriceDesc: 'Cena: od nejvyšší',
      sortAreaDesc: 'Plocha: od největší',
      sortAreaAsc: 'Plocha: od nejmenší',
      sortRoomsDesc: 'Pokoje: nejvíce',
      sortRoomsAsc: 'Pokoje: nejméně',
      sortCommute: 'Nejkratší dojíždění',
      searchLabel: 'Hledat v užším výběru',
      searchPlaceholder: 'Hledat název, cenu, pokoje, plochu…',
      clearSearch: 'Vymazat hledání',
      showingOf: 'Zobrazeno',
      showingOfMid: 'z',
      limitReached: 'Dosáhli jste limitu {n} oblíbených bytů — jeden odeberte, abyste mohli přidat další.',
      capacityTip: 'Váš užší výběr pojme až {n} bytů',
      full: 'plný',
      fullTip: 'Seznam oblíbených je plný ({n}) — jeden odeberte, abyste mohli přidat další',
      clearMenuTip: 'Vymazat oblíbené byty',
      clearOlder7: 'Starší než 7 dní',
      clearOlder14: 'Starší než 14 dní',
      clearOlder30: 'Starší než 1 měsíc',
      clearAll: 'Vymazat všechny oblíbené',
      clearNone: 'Pro tento rozsah není co mazat',
      clearedPrefix: 'Odebráno',
      emptyMatchTitle: 'Žádné výsledky',
      emptyMatchText: 'Ve vašem užším výběru nic neodpovídá. Zkuste jiné hledání nebo filtr.',
      clearFilters: 'Vymazat hledání a filtry',
      clearFilter: 'Zrušit filtr',
      emptyTagTitle: 'S tímto štítkem tu nic není',
      emptyTagText: 'Zkuste jiný štítek, nebo zrušte filtr.',
      lovesIt: 'to miluje',
      notSure: 'váhá',
      tagsFeatures: 'ŠTÍTKY A VLASTNOSTI',
      addTag: 'Přidat štítek',
      addTagChip: 'Štítek',
      removeTag: 'Odebrat štítek',
      tagRemovedToast: 'Štítek odebrán',
      newTagPlaceholder: 'Nový štítek…',
      monthlyRent: 'Měsíční nájem',
      utilities: 'Energie',
      deposit: 'Kauce',
      monthOne: 'měsíc',
      monthMany: 'měsíce',
      perMo: '/měs.',
      perM2: 'ZA M²',
      totalFirstMonth: 'První měsíc celkem',
      shareTitle: 'Sdílejte svůj užší výběr',
      shareText: 'Pozvaní lidé procházejí stejný seznam, reagují a přidávají štítky — společně se rozhodnete rychleji.',
      close: 'Zavřít',
      linkLabel: 'ODKAZ',
      copy: 'Kopírovat',
      anyoneWithLink: 'Kdokoli s odkazem',
      linkCopiedReact: 'Odkaz zkopírován — kdokoli s ním může reagovat a přidávat štítky',
      linkCopiedView: 'Odkaz zkopírován — kdokoli s ním může prohlížet',
      copyFailed: 'Odkaz se nepodařilo zkopírovat — zkopírujte ho ručně',
      roleOwner: 'Vlastník',
      roleReact: 'Může reagovat',
      roleReactSub: 'Reakce, štítky a komentáře',
      roleView: 'Může prohlížet',
      roleViewSub: 'Pouze prohlížení',
      peopleLabel: 'LIDÉ',
      you: 'vy',
      pendingInvite: 'Pozvání odesláno — zatím se nepřipojili',
      removeAccess: 'Odebrat přístup',
      accessRemoved: 'Přístup odebrán',
      invite: 'Pozvat',
      invitePlaceholder: 'Pozvěte e-mailem nebo jménem…',
      inviteSent: 'Pozvánka pro {name} odeslána',
      shareDemoHint: 'Sdílení je ukázka — pozvánky se zatím neodesílají.',
      // Public read-only sharing (replaces the collaborator flow — the
      // invite/role/reaction keys above are orphaned, kept per dict convention)
      shareToWeb: 'Sdílet na web',
      sharingOn: 'Sdílení zapnuto',
      publicChip: 'VEŘEJNÉ',
      shareWebTitle: 'Sdílet na web',
      shareWebAnyone: 'Kdokoli s odkazem',
      shareWebRest: ' si může otevřít váš užší výběr — pouze pro čtení. Bez účtu.',
      whatVisitorsSee: 'CO NÁVŠTĚVNÍCI UVIDÍ',
      visPhotos: 'Fotky, ceny a „kolik zaplatíte“',
      visTags: 'Vaše štítky a čísla bytů (#1, #2…)',
      visAi: 'AI shrnutí',
      visCommute: 'Vaše dojezdová místa — zůstávají soukromá (prozrazují, kde bydlíte a pracujete)',
      visContacts: 'Váš e-mail a kontakty — nikdy se nezobrazí',
      createPublicLink: 'Vytvořit veřejný odkaz',
      unguessableHint: 'Odkaz je náhodný kód — nedá se uhodnout a je skrytý před vyhledávači i roboty. Sdílení můžete kdykoli ukončit.',
      publicLinkLabel: 'VEŘEJNÝ ODKAZ',
      seeVisitors: 'Zobrazit pohled návštěvníka',
      stopSharing: 'Ukončit sdílení',
      stopSharingHint: 'Ukončení sdílení odkaz okamžitě zneplatní. Nové sdílení vytvoří úplně nový odkaz.',
      publicBannerTitle: 'Tato stránka je veřejná',
      publicBannerText: 'Kdokoli s odkazem ji může zobrazit — pouze pro čtení. Dojezdová místa zůstávají soukromá.',
      copyLink: 'Kopírovat odkaz',
      visitorView: 'Pohled návštěvníka',
      linkCopied: 'Odkaz zkopírován',
      nowPublicToast: 'Váš užší výběr je teď veřejný — odkaz zkopírován',
      nowPublicToastNoCopy: 'Váš užší výběr je teď veřejný',
      sharingStoppedToast: 'Sdílení ukončeno — odkaz je neplatný',
      previewBar: 'Prohlížíte veřejnou stránku — přesně to, co uvidí kdokoli s odkazem.',
      exitPreview: 'Ukončit náhled',
      viewOnly: 'POUZE PROHLÍŽENÍ',
      shortlistOf: 'Užší výběr od {name}',
      sharedVia: 'Sdíleno přes Roostr',
      numberTip: 'Byt č. {n} — stálé číslo, snadno se na něj odkážete',
    },
    notifications: {
      title: 'Notifikace',
      signInMessage: 'Přihlaste se a zobrazte upozornění na nové byty z vašich uložených hledání.',
      signIn: 'Přihlásit se',
      allCaughtUp: 'Vše vyřízeno',
      unread: 'nepřečtené',
      markAllRead: 'Označit vše jako přečtené',
      all: 'Vše',
      unreadTab: 'Nepřečtené',
      priceDrops: 'Zlevnění',
      loadError: 'Nepodařilo se načíst notifikace',
      tryAgain: 'Zkusit znovu',
      emptyTitle: 'Zatím žádné notifikace',
      emptyText: 'Nové byty z vašich uložených hledání se zobrazí tady.',
      openListing: 'Otevřít inzerát',
      openFlats: 'Otevřít byty',
      new: 'Nové',
      newMatch: 'nová shoda',
      newMatches: 'nové shody',
      loadMore: 'Načíst více',
      loading: 'Načítání...',
      justNow: 'Právě teď',
      agoMinutes: 'min',
      agoHours: 'h',
      agoDays: 'd',
      today: 'Dnes',
      yesterday: 'Včera',
      earlier: 'Dříve',
      // Roostr Web redesign — What's-new review queue (Phase 7.1)
      whatsNew: 'Co je nového',
      toReview: 'k projití',
      subtitle: 'Čerstvé shody ze všech vašich uložených hledání. Co se vám líbí, si nechte — přistane v Oblíbených. Zbytek přeskočte.',
      allSearches: 'Všechna hledání',
      clearAll: 'Vyčistit vše',
      clearAllTip: 'Přeskočit vše zbývající (lze vrátit)',
      clearedOne: '1 byt přeskočen',
      clearedFew: '{n} byty přeskočeny',
      clearedMany: '{n} bytů přeskočeno',
      undo: 'Vrátit zpět',
      skip: 'Přeskočit',
      skipTip: 'Tento vynechat',
      like: 'Líbí se mi',
      likeTip: 'Přidat do vašich oblíbených',
      summary: 'Shrnutí',
      summaryTip: 'AI shrnutí popisu inzerátu',
      summaryError: 'Shrnutí se nepodařilo vygenerovat.',
      subtitleList: 'Projděte seznam, uložte, co stojí za to, zbytek označte jako viděné — nebo prolistujte jen nejnovějších {n}.',
      newOne: 'nový',
      newFew: 'nové',
      newMany: 'nových',
      modeAll: 'Vše',
      modeSwipe: 'Listovat',
      selected: 'vybráno',
      saveToLiked: 'Uložit do oblíbených',
      markSeen: 'Označit jako viděné',
      cancel: 'Zrušit',
      bulkHint: 'Vyberte řádky pro hromadné akce, nebo:',
      markAllSeen: 'Označit všech {n} jako viděné',
      markAllTip: 'Vynuluje počítadlo — nic se nemaže',
      markDaySeen: 'Označit den jako viděný',
      selectDay: 'Vybrat celý den',
      deselectDay: 'Zrušit výběr dne',
      select: 'Vybrat',
      deselect: 'Zrušit výběr',
      showMore: 'Zobrazit dalších {n}',
      groupToday: 'Dnes',
      groupYesterday: 'Včera',
      groupWeek: 'Tento týden',
      groupEarlier: 'Dříve',
      allSeen: 'vše viděno',
      emptyListTitle: 'Nic tu není',
      emptyScopedA: 'Žádné shody z hledání',
      emptyScopedB: 'zatím.',
      emptyAll: 'Nové shody z vašich uložených hledání se objeví tady.',
      sessionHint: 'V této sadě jen nejnovějších {target} — dalších {n} čeká ve Vše.',
      sessionDoneTitle: 'Nejnovějších {n} projito',
      sessionEmptyTitle: 'Nic nového k listování',
      sessionDoneText: 'Zbývá {n} starších shod — projděte je ve Vše, nebo si vezměte další sadu.',
      swipeMore: 'Listovat dalších {n}',
      backToAll: 'Zpět na Vše',
      utilsShort: 'popl.',
      underMedian: '{n} % pod mediánem',
      overMedian: '{n} % nad mediánem',
      dealTip: 'Cena za m² proti mediánu tohoto seznamu',
      mustHavesMet: '{met}/{n} povinných',
      moreTags: '+{n} štítků',
      lessTags: 'méně',
      openInMaps: 'Otevřít v Google Mapách',
      openPhotoTip: 'Otevřít celý náhled fotky (Enter)',
      stackCapHint: 'Zobrazujeme nejnovějších {n} — starší shody se odsouvají.',
      floorAbbr: 'p.',
      photoCountTip: 'Počet fotografií v inzerátu',
      incomingOne: 'Zobrazit 1 novou shodu',
      incomingFew: 'Zobrazit {n} nové shody',
      incomingMany: 'Zobrazit {n} nových shod',
      seenOne: '1 označen jako viděný',
      seenFew: '{n} označeny jako viděné',
      seenMany: '{n} označeno jako viděné',
      inLiked: 'Ve vašich oblíbených',
      removeFromLiked: 'Odebrat z oblíbených',
      markSeenTip: 'Označit jako viděné — zmizí z počítadla',
      newHomes: 'Nové byty',
      markAllSeenShort: 'Označit vše jako viděné',
      bellMarkAllTip: 'Vynuluje počítadlo — byty zůstanou v Zobrazit vše',
      bellCaughtUp: 'Vše projito — nové shody se objeví tady.',
      viewAll: 'Zobrazit vše',
      bellTip: 'Co je nového — čerstvé shody',
      perM2: 'ZA M²',
      totalFirstMonth: 'První měsíc celkem',
      loadErrorText: 'Při načítání nových shod se něco pokazilo.',
      caughtUpTitle: 'To bylo vše nové',
      caughtUpScopedA: 'Žádné nové byty v hledání',
      caughtUpScopedB: 'Podívejte se na ostatní hledání, nebo se vraťte později.',
      caughtUpAll: 'Vše máte projité. Nové shody se tu objeví, jakmile se najdou.',
      openLiked: 'Otevřít oblíbené',
    },
    about: {
      backToSearch: 'Zpět na hledání',
      title: 'O Roostru',
      subtitle: 'Rychlé, přehledné vyhledávání bytů s AI.',
      whoWeAre: 'Kdo jsme',
      whoWeAreText: 'Jsme dva lidé. Pracujeme z bytů, které jsme dřív hledali složitě — ještě před tím, než vznikl Roostr.',
      whyBuilt: 'Proč jsme to vytvořili',
      whyBuiltText: 'Chtěli jsme službu, která je rychlá, jednoduchá a snadná na používání.',
      nerdyBit: 'Technická stránka',
      nerdyIntro: 'Celý web vznikl s pomocí',
      nerdyMiddle: 'a',
      nerdyOutro: 'Ano, párově jsme programovali s AI.',
      contactText: 'Chcete pozdravit? Napište nám na',
      contactNote: '(Čteme každý e-mail. Jsme na to dva.)',
    },
    pricing: {
      backToSearch: 'Zpět na hledání',
      choosePlan: 'Vyberte si plán',
      subtitle: 'Roostr můžete používat zdarma. Přechodem na Penthouse odemknete všechny funkce.',
      checkoutCanceled: 'Platba byla zrušena. Můžete to zkusit kdykoliv znovu.',
      freeForever: 'Navždy zdarma',
      perMonth: '/měsíc',
      included: 'Zahrnuto',
      currentPlan: 'Aktuální plán',
      getStarted: 'Začít',
      monthly: 'Měsíčně',
      quarterly: 'Čtvrtletně',
      perThreeMonths: '/3 měsíce',
      savePercent: 'Ušetříte',
      loading: 'Načítání...',
      betaFree: 'Zdarma během bety',
      duringBeta: 'během bety',
      everythingPlus: 'Vše z Roostru, plus',
      redirecting: 'Přesměrování...',
      upgrade: 'Přejít na vyšší plán',
      signInToUpgrade: 'Přihlaste se pro upgrade',
      paymentsSafe: 'Platby zpracovává bezpečně Stripe. Platební údaje neukládáme. Předplatné můžete kdykoliv zrušit.',
    },
    listingCard: {
      openInMaps: 'Zobrazit v Google Mapách',
      mustHaveMatch: 'Podmínka — splněno (upravit v Nastavení)',
      roomOne: 'pokoj',
      roomMany: 'pokoje',
      floor: 'Patro',
      rent: 'nájem',
      noPrice: 'Cena neuvedena',
      estTotal: 'Odhad celkem',
      costBreakdown: 'Rozpis nákladů',
      utilities: 'Energie',
      serviceCharges: 'Poplatky',
      parking: 'Parkování',
      internet: 'Internet',
      deposit: 'Kauce',
      monthOne: 'měsíc',
      monthMany: 'měsíce',
      aiNote: 'AI odhad. Nemusí být přesné.',
      more: 'další',
      less: 'méně',
      viewSource: 'Zdroj inzerátu',
      hideListing: 'Skrýt inzerát',
      previousImage: 'Předchozí fotka',
      nextImage: 'Další fotka',
      unavailable: 'Možná už není dostupný',
      monthlyRent: 'Měsíční nájem',
      perMo: '/měs.',
      details: 'Detail',
      openDetails: 'Otevřít detail inzerátu',
      postedOnRoostr: 'Inzerováno na Roostru',
      postedOnRoostrTip: 'Inzerát vložil majitel přímo na Roostr — ve vyhledávání se řadí nad importované inzeráty',
      sourceTip: 'Původní inzerát na',
      liveUpdateTip: 'Aktuální zpráva od majitele — přidána bez změny inzerátu.',
      allFeatures: 'Všechny vlastnosti',
      showAllFeatures: 'Zobrazit všechny vlastnosti',
      filterBy: 'Filtrovat podle',
      like: 'Přidat do oblíbených',
      unlike: 'Odebrat z oblíbených',
      statRooms: 'POKOJE',
      statArea: 'PLOCHA',
      statFloor: 'PODLAŽÍ',
    },
    savedSearches: {
      title: 'Hledání a agenti',
      subtitle: 'Nasaďte osobní agenty pro hledání vašeho dalšího bytu',
      activeOf: 'aktivní',
      loading: 'Načítám hledání...',
      tryAgain: 'Zkusit znovu',
      emptyTitle: 'Zatím žádná uložená hledání',
      emptyText: 'Uložte kritéria hledání pro rychlý přístup a nasazení agentů sledujících nové inzeráty.',
      saved: 'Uloženo',
      showAll: 'Zobrazit vše',
      new: 'Nové',
      startNotifications: 'Zapnout notifikace',
      stopNotifications: 'Vypnout notifikace',
      found7d: 'nalezeno · 7d',
      failedToLoad: 'Nepodařilo se načíst uložená hledání',
      failedToDelete: 'Nepodařilo se smazat hledání',
      failedToUpdate: 'Nepodařilo se aktualizovat notifikaci',
      signInToView: 'Přihlaste se pro zobrazení uložených hledání',
      allDeployed: 'Všech 5 agentů je nasazeno! Odvolejte jednoho pro nasazení dalšího.',
    },
    listingDetail: {
      rateLimitExceeded: 'Překročen limit požadavků',
      notFound: 'Inzerát nenalezen',
      rateLimitHint: 'Poslali jste příliš mnoho požadavků. Chvíli počkejte a zkuste to znovu, nebo se přihlaste pro vyšší limity.',
      notFoundHint: 'Tento inzerát mohl být odstraněn nebo je odkaz neplatný.',
      backToSearch: 'Zpět na hledání',
      copied: 'Zkopírováno!',
      share: 'Sdílet',
      viewOnMarketplace: 'Zobrazit na',
      originalSite: 'původním webu',
      view: 'Otevřít',
      goToImage: 'Přejít na fotku',
      unavailableHint: 'Fotky nešlo načíst z původního zdroje. Prověříme to.',
      visitOriginal: 'Navštívit originál',
      additionalCostsApply: 'Platí dodatečné náklady',
      baseRent: 'Základní nájem',
      utilitiesEstimate: 'Odhad energií',
      internetTv: 'Internet / TV',
      depositRequired: 'Požadovaná kauce',
      aiCostHint: 'Analyzováno AI z popisu inzerátu. Skutečné náklady se mohou lišit.',
      featuresAmenities: 'Vybavení a vlastnosti',
      featuresHint: 'Automaticky extrahováno AI z textu a fotografií inzerátu',
      fromDescription: 'Z popisu inzerátu:',
      detectedFromPhotos: 'Detekováno z fotografií:',
      featuresAccuracyHint: 'AI extrakce nemusí být vždy přesná.',
      description: 'Popis',
      translatedTo: 'Přeloženo do',
      justFacts: 'Jen fakta',
      showFullDescription: 'Zobrazit celý popis',
      showOriginal: 'Zobrazit originál',
      translate: 'Přeložit',
      chooseLanguage: 'Vybrat jazyk',
      translateTo: 'Přeložit do',
      justFactsTitle: 'Shrnout pouze klíčové informace o nemovitosti',
      translatingTo: 'Překládám do',
      summarizing: 'Shrnuji...',
      location: 'Lokalita',
      openInGoogleMaps: 'Otevřít v Google Maps',
      listedOn: 'Inzerováno na',
      lastUpdated: 'Naposledy aktualizováno',
      // Roostr Web redesign — detail page (Phase 5.2)
      back: 'Zpět',
      summarizeAi: 'Shrnout pomocí AI',
      summarizeAiTitle: 'Vygenerovat AI shrnutí popisu inzerátu',
      aiSummary: 'AI shrnutí',
      aiSummaryDisclaimer: 'Vygenerováno AI z textu inzerátu — nemusí být přesné.',
      hideSummary: 'Skrýt',
      features: 'Vybavení',
      commuteFromHere: 'Dojíždění odsud',
      marketPosition: 'Pozice na trhu',
      whatYoullPay: 'Kolik zaplatíte',
      perMo: '/měs.',
      perM2: 'za m²',
      monthlyRent: 'Měsíční nájem',
      utilities: 'Energie',
      deposit: 'Kauce',
      depositMonths: 'Kauce ({n} měs.)',
      totalFirstMonth: 'První měsíc celkem',
      openOn: 'Otevřít na',
      addToLiked: 'Přidat do oblíbených',
      inLiked: 'Ve vašem výběru oblíbených',
      removeFromLiked: 'Odebrat z oblíbených',
      mustHaves: 'Musí mít',
      listedMeta: 'Inzerováno {when} na {marketplace}',
      listedWhenOnly: 'Inzerováno {when}',
      energyClass: 'Energetická třída',
      agoToday: 'dnes',
      agoOneDay: 'před 1 dnem',
      agoDays: 'před {n} dny',
      mapPreview: 'náhled mapy',
    },
    searches: {
      title: 'Uložená hledání',
      subtitle: 'Každé hledání za vás hlídá trh a nové shody ukládá do sekce Co je nového.',
      alerts: 'Upozornění',
      alertsOff: 'Vypnuto',
      alertsDaily: 'Denně',
      alertsInstant: 'Okamžitě',
      alertsToastDaily: 'Jeden souhrn každé ráno',
      alertsToastInstant: 'Upozornění hned, jak se objeví shoda',
      reviewAll: 'Projít všech {n} nových',
      newOne: '1 nový',
      newFew: '{n} nové',
      newMany: '{n} nových',
      savedOn: 'Uloženo {date}',
      pausedHint: 'Upozornění pozastavena',
      openTitle: 'Otevřít toto hledání',
      reviewTitle: 'Projít nové shody',
      menuTitle: 'Možnosti hledání',
      menuOpenInSearch: 'Otevřít ve vyhledávání',
      menuPause: 'Pozastavit upozornění',
      menuResume: 'Obnovit upozornění',
      menuDelete: 'Smazat hledání',
      deletedToast: 'Hledání smazáno',
      deleteFailed: 'Hledání se nepodařilo smazat',
      restoreFailed: 'Hledání se nepodařilo obnovit',
      pausedToast: 'Upozornění pozastavena',
      resumedToast: 'Upozornění obnovena',
      updateFailed: 'Změnu se nepodařilo uložit',
      emptyTitle: 'Zatím žádná uložená hledání',
      emptyText: 'Vyfiltrujte si nabídky podle svého a klepněte na „Uložit hledání" — trh pohlídáme za vás.',
      startSearching: 'Začít hledat',
      signInTitle: 'Přihlaste se a uložte si hledání',
      signInText: 'Uložená hledání hlídají trh a upozorní vás, jakmile se objeví nové shody.',
      signIn: 'Přihlásit se',
      back: 'Zpět',
      notFoundTitle: 'Hledání nenalezeno',
      notFoundText: 'Možná bylo smazáno.',
      reviewOne: 'Projít 1 novou shodu',
      reviewFew: 'Projít {n} nové shody',
      reviewMany: 'Projít {n} nových shod',
      reviewSub: 'Čerstvé od vaší poslední návštěvy — každou si nechte, nebo přeskočte.',
      matchesOne: 'shoda',
      matchesFew: 'shody',
      matchesMany: 'shod',
      sortNewest: 'Nejnovější',
      sortPriceAsc: 'Cena ↑',
      sortPriceDesc: 'Cena ↓',
      sortSize: 'Velikost',
      newBadge: 'NOVÉ',
      perMonth: '/měs.',
      noMatchesTitle: 'Zatím žádné shody',
      noMatchesText: 'Jakmile se objeví byty odpovídající tomuto hledání, uvidíte je tady.',
    },
    commute: {
      unnamed: 'Bez názvu',
      openDirections: 'otevřít trasu v Mapách Google',
      editPlaces: 'Upravit cíle navigace',
      placesTitle: 'Cíle navigace',
      placesText: 'U každého oblíbeného bytu uvidíte skutečnou vzdálenost a dobu cesty na tato místa.',
      labelPlaceholder: 'Popisek',
      addressPlaceholder: 'Adresa',
      removePlace: 'Odebrat místo',
      addPlace: 'Přidat cíl',
      maxPlaces: 'Až 3 cíle',
      done: 'Hotovo',
      updateNote: 'Vzdálenosti se okamžitě aktualizují všude — na kartách v Oblíbených i v detailu inzerátu.',
      straightLineTip: 'přímá vzdálenost (trasa není dostupná)',
      straightLineNote: 'přímá vzdálenost tam, kde nebyla dostupná trasa',
      noListingLocation: 'tento inzerát nemá polohu',
      addressNotFound: 'tuto adresu se nepodařilo najít',
    },
    post: {
      title: 'Vložit inzerát',
      subtitle: 'Šest krátkých kroků. Vše, co napíšete, se samo naformátuje — sledujte náhled.',
      forRent: 'K pronájmu',
      forSale: 'Na prodej',
      s1Title: 'Fotky',
      s1Hint: 'Jediná věc, kterou za vás nikdo neudělá. Všechno ostatní pak bude snazší.',
      photosDrop: 'Přetáhněte fotky sem, nebo je vyberte',
      photosDropHint: 'V libovolném pořadí a kvalitě — samy se seřadí, projasní a zbaví duplicit',
      photosFootnote: 'První fotka je úvodní — tažením změníte pořadí, × fotku odebere, klepnutím na název ho přejmenujete.',
      isCover: 'Úvodní fotka — úvodní je vždy první pozice',
      dragReorder: 'Tažením změníte pořadí — na první pozici se fotka stane úvodní',
      removePhoto: 'Odebrat fotku',
      roomNamePlaceholder: 'Místnost…',
      roomNameLabel: 'Název fotky',
      roomNameTitle: 'Klepnutím fotku přejmenujete',
      roostrBoost: 'Inzeráty vložené na Roostr se řadí nad importované — zájemci uvidí ten váš jako první.',
      uploading: 'nahrávám…',
      scanning: 'Rozpoznávám místnosti, kontroluji světlo a ostrost…',
      fixDarkPrefix: 'Fotka ložnice byla tmavá —',
      fixDarkOn: 'projasněna',
      fixDarkOff: 'ponechána, jak je',
      fixDupPrefix: 'Dva téměř totožné obýváky —',
      fixDupOn: 'ponechán ten ostřejší',
      fixDupOff: 'ponecháváme oba',
      enhance: 'Vylepšit',
      keepBoth: 'Ponechat obě',
      undo: 'Vrátit zpět',
      s2Title: 'Základy',
      s2Hint: 'Pár klepnutí — do deseti sekund. Vybavení rozpoznané na fotkách je předvyplněné.',
      layout: 'Dispozice',
      area: 'Plocha',
      floor: 'Podlaží',
      elevator: 'Výtah',
      yes: 'Ano',
      no: 'Ne',
      spottedInPhotos: 'rozpoznáno na fotkách',
      featuresLabel: 'Vybavení — klepnutím přepnete',
      moreTags: 'Všechny štítky',
      moreTagsTitle: 'Vyberte z čehokoli, podle čeho zájemci filtrují',
      tagPickerTitle: 'Všechny štítky',
      tagPickerHint: 'Vše, podle čeho zájemci filtrují — klepnutím přidáte k inzerátu.',
      tagPickerSearch: 'Hledat štítky…',
      tagPickerEmpty: 'Žádné štítky neodpovídají hledání.',
      tagPickerSelected: 'vybráno',
      tagPickerDone: 'Hotovo',
      tagFallbackGroup: 'Běžné štítky',
      adviceDarkLead: 'Fotka ložnice je tmavá.',
      adviceDarkBody: 'Zkuste ji přefotit za denního světla nebo se všemi světly — světlé fotky mají znatelně víc zobrazení.',
      areaTypeIt: 'zadejte číslo',
      previewLabelCard: 'VAŠE KARTA VE VÝSLEDCÍCH HLEDÁNÍ',
      s3Title: 'Lokalita',
      s3Hint: 'Celá adresa bude v inzerátu, se špendlíkem na mapě.',
      addressPlaceholder: 'Ulice a číslo…',
      verified: 'Ověřeno',
      change: 'Změnit',
      pulledFromMap: 'převzato z mapy',
      highlightsLabel: 'Zajímavosti v okolí — klepnutím kteroukoli odeberete',
      s4Title: 'Cena',
      s4HintAnchored: 'Ukotveno k',
      s4HintComparables: 'srovnatelným inzerátům',
      s4HintBand: 'Zelené pásmo je férové — zbytek je o tom, jak rychle chcete mít pronajato.',
      monthlyRent: 'Měsíční nájem',
      perMonth: '/měs.',
      expect: 'Očekávejte',
      inquiries: 'poptávek',
      aWeek: 'týdně.',
      onTopOfRent: 'Nad rámec nájmu',
      utilities: 'Energie',
      perMonthLabel: '/ měsíc',
      deposit: 'Kauce',
      oneTime: '/ jednorázově',
      autoTwiceRent: 'auto 2× nájem',
      autoRevertTitle: 'Zpět na automatický dvojnásobek nájmu',
      auto: 'auto',
      customFeePlaceholder: 'Za co to je?',
      remove: 'Odebrat',
      extraParking: 'Parkování',
      extraInternet: 'Internet',
      extraCleaning: 'Úklid',
      extraCustom: 'Vlastní…',
      addFee: 'Přidat',
      addCustomFee: 'Přidat vlastní poplatek',
      tenantPays: 'Nájemce platí',
      allInSuffix: '/měs. se vším všudy, plus',
      depositOnce: 'kauce jednorázově.',
      s5Title: 'Příběh',
      s5Hint: 'Pište cokoli — překlepy, caps lock, odrážky. Náhled vždy ukazuje učesanou verzi.',
      titleLabel: 'Název',
      useSuggestedTitle: 'Použít navržený název',
      use: 'Použít',
      descriptionLabel: 'Popis',
      descriptionPlaceholder: '3–5 vět je ideál. Co máte na bydlení tady rádi? Kontakty vynechte — na ty je krok Kontaktní údaje.',
      rephrase: 'Přeformulovat:',
      toneWarm: 'Vřelý',
      toneFactual: 'Věcný',
      tonePunchy: 'Úderný',
      rewriteVoice: 'Přepsat:',
      draftVoice: 'Napsat návrh za mě:',
      voice: 'tón',
      toneEmptyHint: '— nebo nechte první verzi napsat některým z nich',
      pasteDemo: 'Vložit můj starý inzerát (demo)',
      tidiedPrefix: 'Uklizeno za běhu — odstraněno:',
      tidiedSuffix: 'Náhled ukazuje čistou verzi.',
      applyHereToo: 'Použít i tady',
      s6Title: 'Kontaktní údaje',
      s6Hint: 'Jak se vám lidé ozvou — zobrazí se přímo v inzerátu.',
      email: 'E-mail',
      fromYourAccount: 'z vašeho účtu',
      phone: 'Telefon',
      telegram: 'Telegram',
      instagram: 'Instagram',
      scheduleLabel: 'Zobrazovat kontakty jen v určité časy',
      always: 'Vždy',
      scheduled: 'Podle rozvrhu',
      from: 'Od',
      visibleFrom: 'Viditelné od',
      to: 'do',
      visibleUntil: 'Viditelné do',
      scheduleFootnote: 'Mimo tyto hodiny jsou vaše kontakty v inzerátu skryté.',
      publish: 'Zveřejnit inzerát',
      readyNote: 'Hotovo — kdykoli můžete inzerát pozastavit nebo upravit v Mých inzerátech.',
      stillNeeds: 'Ještě chybí:',
      missPhotos: 'fotky',
      missLayout: 'dispozice',
      missArea: 'plocha',
      missFloor: 'podlaží',
      missAddress: 'adresa',
      missStory: 'pár vět příběhu',
      missContact: 'kontakt na vás',
      previewFootnote: 'Přeformátovává se živě, jak píšete — přesně tohle uvidí zájemci',
      details: 'Detaily',
      preview: 'Náhled',
      previewHeader: 'CO UVIDÍ ZÁJEMCI',
      previewEnhanced: '1 fotka vylepšena',
      previewPhotosPlaceholder: 'SEM PŘIJDOU VAŠE FOTKY',
      postedOnRoostr: 'Inzerováno na Roostru',
      previewLabelTitle: 'NÁZEV — VZNIKÁ V KROKU PŘÍBĚH',
      previewLabelLocation: 'LOKALITA',
      previewLabelPrice: 'CENA — NASTAVENA PODLE SKUTEČNÝCH SROVNÁNÍ',
      previewUtilities: 'energie',
      previewDeposit: 'kauce',
      previewLabelFeatures: 'VYBAVENÍ — VYČTENO Z FOTEK A VAŠEHO TEXTU',
      previewLabelStory: 'PŘÍBĚH — ZOBRAZÍ SE NA DETAILU',
      previewLabelContact: 'KONTAKT',
      previewContactsVisible: 'Kontakty viditelné',
      publishedTitle: 'Je to venku!',
      publishedBody: 'Váš inzerát teď vidí 1 842 lidí hledajících na Vinohradech. První uložení obvykle přijdou během pár hodin.',
      openMyListings: 'Otevřít Moje inzeráty',
      postAnother: 'Vložit další',
    },
    myListings: {
      title: 'Moje inzeráty',
      subtitleLive: 'aktivní',
      savesBySeekers: 'uložení od zájemců',
      postListing: 'Vložit inzerát',
      assistantTipsSingular: 'věc, kterou by asistent změnil — je to na jedno klepnutí.',
      assistantTipsPlural: 'věci, které by asistent změnil — každá je na jedno klepnutí.',
      emptyTitle: 'Zatím tu nic není',
      emptyText: 'Vložte svůj první inzerát — většinu psaní zvládne asistent.',
      statusLive: 'Aktivní',
      statusPaused: 'Pozastaveno',
      statusDraft: 'Koncept',
      statusRented: 'Pronajato',
      newBadge: 'NOVÉ',
      actions: 'Možnosti inzerátu',
      askingPerMonth: 'požadováno / měsíc',
      askingPrice: 'požadovaná cena',
      completeness: 'Úplnost inzerátu',
      edit: 'Upravit inzerát',
      editSub: 'Znovu otevře editor se vším předvyplněným',
      pause: 'Pozastavit inzerát',
      pauseSub: 'Skrytý ve vyhledávání, statistiky zůstávají',
      pausedToast: 'Inzerát pozastaven',
      resume: 'Obnovit inzerát',
      resumedToast: 'Inzerát obnoven — je zpět ve výsledcích hledání',
      backInSearch: 'Zpět ve výsledcích hledání',
      markRented: 'Označit jako pronajato',
      rentedToast: 'Gratulujeme! 🎉 Označeno jako pronajato',
      relist: 'Znovu zveřejnit',
      relistedToast: 'Znovu zveřejněno',
      delete: 'Smazat',
      deletedToast: 'Inzerát smazán',
      dismiss: 'Odmítnout',
      priceUpdatedTo: 'Cena změněna na',
      shareCopied: 'Odkaz ke sdílení zkopírován',
      liveUpdates: 'AKTUÁLNÍ ZPRÁVY',
      liveUpdatesHint: 'připnuto na inzerátu, popis zůstává beze změny',
      liveUpdate: 'AKTUÁLNÍ ZPRÁVA',
      liveUpdateHint: 'jedna krátká zpráva připnutá na inzerátu',
      oneUpdateNote: 'Vždy jen jedna zpráva — pro novou nejdřív odeberte tuto.',
      promoTitle: 'Propagovat inzerát',
      promoManage: 'Spravovat propagaci',
      promoSubtitle: 'Pět výhod na jeden týden, jedna platba. Skončí automaticky — žádné automatické obnovování. Toto demo nic neúčtuje.',
      promoBadge: 'PROPAGOVANÝ',
      promoActive: 'AKTIVNÍ',
      promoActivate: 'Propagovat na 1 týden',
      promoWeek: 'týden',
      promoDemo: 'demo, nic se neúčtuje',
      promoEnd: 'Ukončit dřív',
      promoEndsIn: 'Skončí za',
      promoDaysLong: 'dní',
      promoAutoStop: 'skončí automaticky, bez obnovování',
      promoActivatedToast: 'Propagováno na 7 dní — skončí automaticky, bez obnovování (demo, zdarma)',
      promoEndedToast: 'Propagace ukončena',
      promoDeckTitle: 'V čele swipe balíčku',
      promoDeckDesc: 'Garantované místo mezi prvními kartami denního balíčku u každého hledajícího, jehož hledání odpovídá.',
      promoDeckActive: 'Běží — zobrazuje se v prvních 3 kartách odpovídajících balíčků',
      promoStripTitle: 'Zvýrazněná tečka v cenovém grafu',
      promoStripDesc: 'Váš inzerát vynikne přímo v cenovém grafu na stránce hledání — korálový, s prstencem, s mini kartou.',
      promoStripActive: 'Běží v každém odpovídajícím hledání',
      promoBumpTitle: 'Oživení inzerátu — každé 3 dny',
      promoBumpDesc: 'Jedním klepnutím se inzerát řadí jako čerstvě vložený. Žádné mazání ani přepisování, uložení zůstávají.',
      promoBumpOld: 'před 24 dny',
      promoBumpNew: 'PRÁVĚ TEĎ',
      promoBumpNow: 'Oživit teď',
      promoBumpIn: 'Další oživení za',
      promoBumpDays: 'd',
      promoBumpedToast: 'Inzerát oživen — řadí se jako nový',
      promoAbTitle: 'A/B test úvodní fotky a titulku',
      promoAbDesc: 'Automaticky střídáme úvodní fotku a titulek, měříme prokliky a necháme vyhrát lepší variantu.',
      promoAbActive: 'Testuje se — vede varianta B (6,3 % vs 4,1 % CTR)',
      promoSocialTitle: 'Sociální důkaz na kartě',
      promoSocialDesc: 'Skutečná čísla poptávky přímo na kartě — vzácnost hýbe nájemci rychleji než jakýkoli odznak. Volitelné, nikdy smyšlené.',
      promoSocialActive: 'Běží —',
      promoSocialShort: 'v užším výběru',
      promoSocialViewings: 'žádostí o prohlídku',
      editPrice: 'Upravit požadovanou cenu',
      editPrices: 'Upravit nájem, energie, kauci i poplatky',
      pricesLabel: 'CENY',
      pricesHint: 'změny se projeví okamžitě',
      viewTitle: 'Jak to vidí zájemci',
      viewInSearch: 'Ve výsledcích',
      viewDetail: 'Detail',
      removeUpdate: 'Odebrat zprávu',
      updatePlaceholder: 'např. Prohlídky tuto sobotu · Volné od 1. září …',
      updatePosted: 'Aktuální zpráva přidána — připnuta v horní části inzerátu',
      post: 'Zveřejnit',
    },
  },
  en: {
    header: {
      likedFlats: 'Liked flats',
      planner: 'Planner',
      commandSearch: 'Search',
      savedSearches: 'Saved searches',
      searches: 'Searches',
      explore: 'Explore',
      liked: 'Liked',
      myListings: 'My listings',
      post: 'Post',
      postTip: 'Post a listing — the assistant builds it with you',
      myListingsTip: 'My listings — post and manage from one place',
    },
    regionalSettings: {
      title: 'Regional settings',
      language: 'Language',
    },
    footer: {
      about: 'About',
      privacy: 'Privacy',
      terms: 'Terms',
      twitter: 'Twitter',
      email: 'Email',
    },
    home: {
      title: 'Find your perfect apartment',
      subtitle: "Search apartments like you'd text a friend. Natural language, smart filters, instant results.",
      termsIntro: 'By searching, you agree to our',
      termsLink: 'Terms of Service',
    },
    palette: {
      placeholder: 'Search homes or type a command...',
      noResults: 'No results found.',
      searchGroup: 'Search',
      searchFor: 'Search homes for',
      recent: 'Recent searches',
      savedGroup: 'Searches & Alerts',
      new: 'new',
      saveSearch: 'Save current search',
      openSaved: 'Open Searches & Alerts',
      thisSearch: 'This search',
      clearFilters: 'Clear all filters',
      active: 'active',
      toMap: 'Switch to Map view',
      toList: 'Switch to List view',
      toSale: 'Switch to homes for sale',
      toRent: 'Switch to homes for rent',
      expandAll: 'Expand all descriptions',
      collapseAll: 'Collapse all descriptions',
      showHidden: 'Show hidden listings',
      copyLink: 'Copy link to this search',
      copied: 'Search link copied',
      copyFailed: 'Could not copy the link',
      shortcuts: 'Browse results with your keyboard',
      sortGroup: 'Sort results',
      goTo: 'Go to',
      gotoSearch: 'Search',
      lastSearch: 'Back to my last search',
      gotoLiked: 'Liked listings',
      gotoSearches: 'My searches',
      gotoNotifications: 'Notifications',
      gotoMyListings: 'My listings',
      gotoPost: 'Post a listing',
      gotoSettings: 'Settings',
      gotoPricing: 'Pricing',
      gotoAbout: 'About',
      language: 'Language',
      appearance: 'Appearance',
      switchTo: 'Switch to',
      mode: 'mode',
      theme: {
        light: 'Light',
        dark: 'Dark',
        hacker: 'Hacker',
        system: 'System',
      },
      current: 'current',
      helpGroup: 'Help & account',
      tour: 'How Roostr works',
      signIn: 'Sign in or create an account',
      signOut: 'Sign out',
      navigate: 'Navigate',
      select: 'Select',
      close: 'Close',
    },
    gate: {
      hook: 'Instant alerts',
      headline1: 'Be first',
      headline2: 'through the door.',
      intro1: 'The best flats in Czechia let in ',
      introStrong: 'within hours',
      intro2: '. Roostr pings you the second one matches — and finds it with AI that reads every listing for you.',
      ctaLead: 'Save your searches and get instant alerts',
      google: 'Continue with Google',
      create: 'Create your account',
      already: 'Already with us?',
      signIn: 'Sign in',
      chips: {
        rooms2: '2+ rooms',
        under25k: 'under 25k',
        balcony: 'balcony',
        terrace: 'terrace',
        furnished: 'furnished',
        dishwasher: 'dishwasher',
        washer: 'washing machine',
        ac: 'air conditioning',
        elevator: 'elevator',
        bathtub: 'bathtub',
        parking: 'parking',
        pets: 'pet friendly',
      },
      peek: {
        eyebrow: 'A look inside',
        homes: ' live homes, ',
        one: 'one',
        yours: ' is yours.',
        sub: 'Fresh today across Czechia. Sign in to save your shortlist.',
      },
      unlock: 'Unlock {n} more',
      thousands: 'thousands',
      story: {
        eyebrow: 'Why renters love Roostr',
        title1: 'Everything, ',
        title2: 'working for you.',
      },
      features: {
        alerts: {
          eyebrow: 'Instant alerts',
          title: 'The moment a match goes live',
          body: 'New flats vanish fast. Save a search once and Roostr watches the market 24/7 — pinging you the second something fits, before anyone else looks.',
        },
        ai: {
          eyebrow: 'AI search',
          title: "Search like you'd text a friend",
          body: '“2+kk with a balcony, in Holešovice, under 25k total including bills.” Roostr understands price, rooms, location and amenities all at once — no fiddly filters.',
        },
        swipe: {
          eyebrow: 'Swipe to triage',
          title: 'Only see what truly fits',
          body: 'New matches arrive as a tidy deck. Swipe right to keep, left to skip — a minute a day and your shortlist builds itself.',
        },
        breakdown: {
          eyebrow: 'Price breakdown',
          title: 'The real monthly cost, itemised',
          body: "No surprises at signing. Roostr reads each listing's description and pulls out rent, utilities and deposit — so you see the true all-in cost without digging through the fine print.",
        },
      },
      why: {
        title: 'Why create an account?',
        body1: 'Searching is free, no login needed. But an account ',
        strong1: 'works for you',
        body2: ' — it saves your searches and Roostr watches the market 24/7, so it ',
        strong2: 'pings you the instant',
        body3: ' a new flat matches. Your shortlist stays synced across every device, too.',
        chip1: 'Saved searches',
        chip2: 'Instant alerts',
        chip3: 'Synced everywhere',
      },
      footer: 'Bot-free AI · No spam · Made in Prague',
      visual: {
        alertTitle: 'New match — The Quiet One',
        alertBody: 'Bright 2+kk near Riegrovy sady · 23 500 Kč',
        now: 'now',
        swipeTitle: 'Cozy 2+kk, Žižkov',
        baseRent: 'Base rent',
        utilities: 'Utilities & services',
        totalMonth: 'Total / month',
        depositNote: '+ 47 000 Kč deposit (2 months), one-time',
      },
      card: {
        monthlyRent: 'Monthly rent',
        utilities: 'Utilities',
        deposit: 'Deposit (2 months)',
        perMonth: '/mo',
        perM2: 'PER M²',
        totalFirstMonth: 'Total first month',
        min: 'min',
        transit: 'Transit',
        mustHaves: 'MUST-HAVES',
        features: 'FEATURES',
      },
    },
    settings: {
      back: 'Back',
      title: 'Settings',
      profile: 'Profile',
      appearance: 'Appearance',
      searchDefaults: 'Search Defaults',
      language: 'Language',
      notifications: 'Notifications',
      subscription: 'Subscription',
      privacyAndData: 'Privacy & Data',
      profileHeading: 'Profile',
      profileDescription: 'Manage your account information.',
      photoSyncedFromGoogle: 'Photo synced from Google account',
      displayName: 'Display name',
      yourName: 'Your name',
      saved: 'Saved!',
      saving: 'Saving...',
      save: 'Save',
      email: 'Email',
      accountEmail: 'Account email',
      fromGoogle: 'From Google',
      password: 'Password',
      resetLinkSent: 'Reset link sent!',
      changePassword: 'Change password',
      resetEmailSentTo: 'We sent a reset link to',
      resetEmailHint: "We'll send a password reset link to your email.",
      appearanceHeading: 'Appearance',
      appearanceDescription: 'Customize how Roostr looks and feels.',
      theme: 'Theme',
      light: 'Light',
      dark: 'Dark',
      hacker: 'Hacker',
      system: 'System',
      autoDetect: 'Auto-detect',
      defaultSearchView: 'Default search view',
      defaultSearchViewHint: 'Which view to show when you open search results.',
      listingCardDensity: 'Listing card density',
      listingCardDensityHint: 'Compact mode shows smaller cards so you can scan more listings at once.',
      comfortable: 'Comfortable',
      compact: 'Compact',
      largerCards: 'Larger cards',
      moreResults: 'More results',
      websiteLanguage: 'Website language',
      websiteLanguageHint: 'Interface language for buttons, labels, and menus.',
      languageTitle: 'Language',
      languageCardHint: 'The interface language, and the language AI features respond in.',
      aiSummaryLanguage: 'AI summary language',
      aiSummaryLanguageHint: 'AI summaries of listing descriptions are written in this language.',
      secLanguage: 'Language',
      searchDefaultsHeading: 'Search Defaults',
      searchDefaultsDescription: 'Set defaults that apply to every new search.',
      defaultCity: 'Default city',
      defaultCityHint: 'Pre-selected city when you start a new search.',
      defaultSortOrder: 'Default sort order',
      defaultSortOrderHint: 'How search results are ordered by default.',
      showEstimatedTotal: 'Show total estimated price',
      showEstimatedTotalHint: 'When enabled, prices include estimated utilities and fees.',
      listingFreshness: 'Listing freshness',
      listingFreshnessHint: 'Only show listings posted within this time period.',
      dealbreakerFilters: 'Dealbreaker filters',
      dealbreakerFiltersHint: 'Permanent requirements that apply to all searches.',
      mustHave: 'Must have',
      mustHaveHint: 'Listings must include all of these features.',
      searchFeaturesToAdd: 'Search features to add...',
      sortRelevance: 'Relevance',
      sortNewest: 'Newest first',
      sortOldest: 'Oldest first',
      sortPriceAsc: 'Price: low to high',
      sortPriceDesc: 'Price: high to low',
      sortAreaAsc: 'Area: small to large',
      sortAreaDesc: 'Area: large to small',
      sortRoomsAsc: 'Rooms: few to many',
      sortRoomsDesc: 'Rooms: many to few',
      freshness7: 'Last 7 days',
      freshness14: 'Last 14 days',
      freshness30: 'Last 30 days',
      freshnessAny: 'Any age',
      notificationsHeading: 'Notifications',
      notificationsDescription: 'Choose how you want to be notified about new listings and price changes.',
      notificationChannels: 'Notification channels',
      emailNotifications: 'Email notifications',
      emailNotificationsHint: 'Receive updates about your saved searches via email.',
      pushNotifications: 'Push notifications',
      pushNotificationsHint: 'Browser push notifications for instant alerts.',
      whatsappNotifications: 'WhatsApp',
      whatsappNotificationsHint: 'Get new listing alerts directly on WhatsApp.',
      comingSoon: 'Coming soon',
      frequency: 'Frequency',
      frequencyHint: 'How often should we send you notifications?',
      frequencyInstant: 'Instant',
      frequencyInstantHint: 'As they happen',
      frequencyDaily: 'Daily digest',
      frequencyDailyHint: 'Once a day',
      frequencyWeekly: 'Weekly summary',
      frequencyWeeklyHint: 'Once a week',
      frequencyPerSearchHint: 'Each saved search has its own alert frequency.',
      frequencyPerSearchLink: 'Manage it on your saved searches',
      // Roostr card-stack settings page (web/settings.jsx)
      profileHint: 'Prefills the contact step when you post a listing.',
      phone: 'Phone',
      phoneHint: 'Optional — shown only where you allow it',
      notificationsHint: 'How new matches from your saved searches reach you.',
      manageOnSearches: 'Saved searches',
      secProfile: 'Profile',
      secNotifications: 'Notifications',
      secAppearance: 'Appearance',
      secPreferences: 'Search & preferences',
      secSubscription: 'Subscription',
      secAccount: 'Account',
      trackedDestinations: 'Tracked destinations',
      trackedDestinationsHint: 'Every listing shows the commute to these. Up to 3.',
      addDestination: 'Add a destination',
      destinationLabel: 'Label (Gym, Parents…)',
      destinationLabelShort: 'Label',
      destinationAddress: 'Address',
      destinationsFull: '3 of 3 — remove one to track another place.',
      removeDestination: 'Remove destination',
      add: 'Add',
      mustHaves: 'Must-haves',
      mustHavesHint: 'Homes are scored against these everywhere. Up to 5.',
      mustHavesFull: 'Five is the limit — drop one first',
      mustHavesSearch: 'Search all features…',
      noFeaturesMatch: 'No features match your search.',
      chooseMustHaves: 'Choose must-haves',
      editMustHaves: 'Add or edit',
      done: 'Done',
      selectedSuffix: 'selected',
      loadingFeatures: 'Loading features…',
      account: 'Account',
      signOut: 'Sign out',
      deleteAccountSub: 'Removes your profile, listings and saved searches',
      deleteEllipsis: 'Delete…',
      confirmDelete: 'Yes, delete',
      deleteFailed: 'Could not delete the account. Please try again.',
      quietHours: 'Quiet hours',
      quietHoursHint: 'Pause notifications during specific hours.',
      from: 'From',
      until: 'Until',
      to: 'to',
      privacyHeading: 'Privacy & Data',
      privacyDescription: 'Manage your data, search history, and privacy preferences.',
      searchHistory: 'Search history',
      searchHistoryHint: 'Save your recent searches for quick access.',
      cleared: 'Cleared!',
      clearing: 'Clearing...',
      clearSearchHistory: 'Clear all search history',
      clearAction: 'Clear',
      exportData: 'Export your data',
      exportDataHint: 'Download all your data as a JSON file.',
      exportAction: 'Download',
      downloaded: 'Downloaded!',
      exporting: 'Exporting...',
      exportAsJson: 'Export data as JSON',
      deleteAccount: 'Delete account',
      deleteAccountHint: 'Permanently delete your account and all associated data. This cannot be undone.',
      deleteMyAccount: 'Delete my account',
      deleting: 'Deleting...',
      confirmDeleteAll: 'Yes, delete everything',
      cancel: 'Cancel',
      subscriptionHeading: 'Subscription',
      subscriptionDescription: 'Manage your Roostr subscription plan.',
      welcomePenthouse: 'Welcome to Penthouse! Enjoy all premium features.',
      active: 'Active',
      activating: 'Activating...',
      freePlan: 'Roostr (Free)',
      cancelsOn: 'Cancels on',
      renewsOn: 'Renews on',
      manageSubscription: 'Manage Subscription',
      redirecting: 'Redirecting...',
      activatingMessage: 'Payment received - activating your subscription...',
      delayedActivation: 'Taking a bit longer than usual. Try refreshing in a moment.',
      refreshPage: 'Refresh page',
      upgradeHint: 'Upgrade to Penthouse to unlock all features.',
      premiumFeatures: 'Premium features',
      upgradeToPenthouse: 'Upgrade to Penthouse',
      viewPricing: 'View pricing',
      openPortalFailed: 'Failed to open subscription portal.',
      startCheckoutFailed: 'Failed to start checkout.',
      stripeNote: 'Payments are processed securely by Stripe. We never store your payment information.',
      appleManageNote: 'Billed through Apple. Manage or cancel in the App Store on your iPhone (Settings → your name → Subscriptions).',
      appleBillingNote: 'Payments are processed by Apple via the App Store. We never store your payment information.',
    },
    kbd: {
      nextPrev: 'next / prev',
      photos: 'photos',
      summary: 'summary',
      like: 'like',
      allKeys: 'all keys',
      title: 'Keyboard shortcuts',
      subtitle: 'Browse results without touching the mouse',
      move: 'Move',
      actOn: 'Act on the focused card',
      startBrowsing: 'Start browsing',
      nextListing: 'Next listing',
      nextPrevLong: 'Next / previous',
      photosLong: 'Photos',
      stop: 'Stop browsing',
      summaryLong: 'AI summary',
      likeLong: 'Like',
      hideLong: 'Hide',
      open: 'Open details',
      openNewTab: 'Open in a new tab',
    },
    userMenu: {
      signIn: 'Sign in',
      searchesAgents: 'Searches & Agents',
      savedSearches: 'Saved searches',
      myListings: 'My listings',
      likedListings: 'Liked Listings',
      commandPalette: 'Command palette',
      settings: 'Settings',
      signOut: 'Sign out',
    },
    search: {
      list: 'List',
      map: 'Map',
      quickRooms: 'Rooms',
      quickRoomsUnit: 'rooms',
      quickPrice: 'Price',
      quickDistrict: 'District',
      chipGroupRooms: 'Rooms',
      chipGroupPrice: 'Price',
      chipGroupFeatures: 'Features',
      chips: {
        rooms1: '1 room',
        rooms2: '2 rooms',
        rooms3: '3 rooms',
        rooms4plus: '4+ rooms',
        under15k: 'under 15k',
        under20k: 'under 20k',
        under30k: 'under 30k',
      },
      voiceStart: 'Search by voice',
      voiceStop: 'Stop dictation',
      viewAsList: 'View results as list',
      viewOnMap: 'View results on map',
      sortBy: 'Sort:',
      changeSort: 'Change sort order',
      filters: 'Filters',
      searchApartmentsPlaceholder: 'Search apartments...',
      areas: 'areas',
      apartment: 'apartment',
      room: 'room',
      house: 'house',
      rent: 'Rent',
      sale: 'Sale',
      searchButton: 'Search',
      examplePlaceholder: '2 rooms, Praha 7, below 20k...',
      typeLabel: 'Type:',
      transactionLabel: 'Transaction:',
      cityLabel: 'City:',
      select: 'Select',
      any: 'Any',
      districtLabel: 'District:',
      district: 'District',
      districtAll: 'All',
      searchCities: 'Search cities...',
      noCitiesFound: 'No cities found',
      searchDistricts: 'Search districts...',
      typeToSearchDistricts: 'Type to search districts...',
      noDistrictsFound: 'No districts found',
      clearAll: 'Clear all',
      recentSearches: 'Recent searches',
      matchingSearches: 'Matching searches',
      noMatchingSearches: 'No matching searches',
      hideExamples: 'Hide examples',
      moreExamples: 'More examples',
      howItWorks: 'How it works',
      features: 'Features',
      allFilters: 'All Filters',
      clearFilters: 'Clear filters',
      rooms: 'Rooms',
      price: 'Price',
      area: 'Area',
      estTotalTooltip: 'Estimated total includes rent, utilities, service charges, parking, and internet — extracted by AI from listing descriptions. Listings without this data use base rent.',
      upTo: 'up to',
      errorTitle: 'Search Error',
      dismissError: 'Dismiss error',
      searching: 'Searching...',
      apartmentsFound: 'apartments found',
      resultsFor: 'Results for:',
      semanticHint: 'Semantic search: results matched by meaning',
      removeSemantic: 'Remove semantic query',
      removeFilter: 'Remove',
      filter: 'filter',
      mustHaveTitle: 'Dealbreaker filter (must have) — click to edit in Settings',
      excludedTitle: 'Dealbreaker filter (excluded) — click to edit in Settings',
      freshnessTitle: 'Freshness filter — click to edit in Settings',
      lastDays: 'Last',
      days: 'days',
      saveThisSearch: 'Save this search',
      signInToSaveSearches: 'Sign in to save searches',
      saveSearch: 'Save Search',
      saveSearchTitle: 'Save Search',
      saveSearchDescription: 'Save your current search criteria to quickly access it later.',
      searchName: 'Search name',
      searchNamePlaceholder: 'Enter a name for this search',
      searchCriteria: 'Search criteria',
      deployAgentsHint: 'Deploy agents from',
      searchesAndAgents: 'Searches & Agents',
      cancel: 'Cancel',
      saving: 'Saving...',
      turnOnNotifications: 'Turn on notifications',
      notificationsHint: 'Get notified when new flats matching this search are found.',
      alertsLabel: 'Alerts',
      cadenceOffHint: "No alerts — new matches still collect in What's new.",
      signInToSave: 'Please sign in to save searches',
      enterSearchName: 'Please enter a name for this search',
      failedToSave: 'Failed to save search. Please try again.',
      searchLimitReached: "You've reached the {n} saved-search limit — delete one to add another.",
      manageSearches: 'Manage searches',
      queryLabel: 'Query',
      filtersLabel: 'Filters',
      travelLabel: 'Travel',
      sortLabel: 'Sort',
      cardSize: 'Card size',
      of: 'of',
      previousPage: 'Previous page',
      nextPage: 'Next page',
      noResultsToShow: 'No results to show',
      load: 'Load',
      signInToContinue: 'Please sign in to continue searching.',
      genericError: 'Something went wrong. Please try again.',
      connectionError: 'Unable to connect to search. Please check your connection and try again.',
      // Roostr Web redesign — toolbar, insights strip, map view, save-search dialog (Phase 7.1)
      grid: 'Grid',
      hidden: 'hidden',
      hiddenListings: 'Hidden listings',
      hiddenListing: 'Hidden listing',
      clickToUnhide: 'Click to unhide',
      unhideAll: 'Unhide all',
      sortedBy: 'sorted by',
      page: 'Page',
      pageAbbr: 'p.',
      close: 'Close',
      jumpToDistrict: 'Jump to district…',
      jumpByPrice: 'Jump by price',
      jumpByDate: 'Jump by date',
      jumpByArea: 'Jump by area',
      jumpByRooms: 'Jump by layout',
      whatItWatches: 'What it watches',
      allOfCity: 'All of Praha',
      matchesRightNow: 'matches right now',
      mapUnavailableTitle: 'Map is taking a break',
      mapUnavailableText: 'The map service is unavailable right now — browse results in the grid instead.',
      backToGrid: 'Back to grid',
      onMap: 'on map',
      unmappable: '{n} without location',
      unmappableHint: '{n} matching listings have no location and cannot be shown on the map',
      inView: 'In view: {n}',
      inViewCapped: 'In view: {total} · showing first {shown}',
      inViewFollows: 'follows the map',
      districtBack: 'Back to map view',
      districtShowMore: 'Show more ({shown} of {total})',
      panelOpen: 'Show list',
      panelClose: 'Hide the list — press [',
      panelResize: 'Resize list',
      inViewHint: 'The list follows the map — pan or zoom to change what is shown here.',
      noResultsInView: 'No listings in this part of the map',
      noResultsInViewHint: 'Zoom out or move the map to see more.',
      lensMustHave: 'Must-have',
      lensMustHaveHint: 'how many of your must-haves each home meets',
      lensHas: 'Has',
      lensNoMustHaves: 'None of your must-haves',
      lensMissing: 'missing',
      lensPriceHint: 'monthly rent · thousands Kč',
      lensAreaHint: 'apartment size',
      lensRooms: 'Rooms',
      lensRoomsHint: 'rooms + kitchenette',
      districtHoverHint: 'Hover to spot this district on the map',
      home: 'home',
      homeFew: 'homes',
      homes: 'homes',
      medianRentIn: 'Median rent in',
      medianShort: 'med',
      km: 'km',
      centre: 'centre',
      kmToCentreTitle: 'Straight-line distance to the city centre',
      perMonth: '/mo',
      like: 'Like',
      unlike: 'Remove from liked',
      details: 'Details',
    },
    filterModal: {
      searchFilters: 'Search filters…',
      locationType: 'Location & type',
      districts: 'Districts',
      selectedCount: '({n} selected)',
      searchDistrictsIn: 'Search districts in {city}…',
      removeDistrict: 'Remove district',
      filterBy: 'Filter by',
      rentOnly: 'Rent only',
      inclUtilities: 'Incl. utilities',
      roomsHintEmpty: 'Tap a number — tap a second one to make a range.',
      roomsHintExact: 'Exactly {rooms} — tap another number for a range.',
      roomsHintRange: '{lo} to {rooms}.',
      roomOne: 'room',
      roomFew: 'rooms',
      roomMany: 'rooms',
      loadingFilters: 'Loading filters…',
      noFiltersFound: 'No filters found',
      tryDifferentTerm: 'Try a different search term.',
      showHomesOne: 'Show {n} home',
      showHomesFew: 'Show {n} homes',
      showHomesMany: 'Show {n} homes',
      showResults: 'Show results',
      close: 'Close',
      minValue: 'Minimum',
      maxValue: 'Maximum',
      minShort: 'Min',
      maxShort: 'Max',
    },
    liked: {
      title: 'Liked Listings',
      signInMessage: 'Sign in to see your liked listings.',
      signIn: 'Sign in',
      loadError: 'Failed to load liked listings',
      tryAgain: 'Try Again',
      emptyTitle: 'No liked listings yet',
      emptyText: "Click the heart icon on listings to like them and they'll appear here.",
      browseListings: 'Browse listings',
      countOne: 'listing',
      countMany: 'listings',
      emptyHint: 'Your liked apartments will appear here.',
      // Roostr Web redesign — collaborative shortlist (Phase 7.1)
      headerTitle: 'Liked',
      homeOne: 'home',
      homeFew: 'homes',
      homeMany: 'homes',
      subtitle: 'Numbered so it’s easy to talk about — “what about #3?” just works.',
      shareShortlist: 'Share shortlist',
      removedToast: 'Removed from your shortlist',
      removeFromShortlist: 'Remove from shortlist',
      openDetails: 'Open listing details',
      signInTitle: 'Sign in to see your liked homes',
      sort: 'Sort',
      sortNewest: 'Newest first',
      sortPriceAsc: 'Price: low to high',
      sortPriceDesc: 'Price: high to low',
      sortAreaDesc: 'Area: largest first',
      sortAreaAsc: 'Area: smallest first',
      sortRoomsDesc: 'Rooms: most first',
      sortRoomsAsc: 'Rooms: fewest first',
      sortCommute: 'Shortest commute',
      searchLabel: 'Search your shortlist',
      searchPlaceholder: 'Search title, price, rooms, area…',
      clearSearch: 'Clear search',
      showingOf: 'Showing',
      showingOfMid: 'of',
      limitReached: 'You’ve reached the {n} liked-homes limit — remove one to add another.',
      capacityTip: 'Your shortlist holds up to {n} homes',
      full: 'full',
      fullTip: 'Liked list full ({n}) — remove one to add another',
      clearMenuTip: 'Clear liked homes',
      clearOlder7: 'Older than 7 days',
      clearOlder14: 'Older than 14 days',
      clearOlder30: 'Older than 1 month',
      clearAll: 'Clear all likes',
      clearNone: 'Nothing to clear for that range',
      clearedPrefix: 'Removed',
      emptyMatchTitle: 'No matches',
      emptyMatchText: 'Nothing in your shortlist matches. Try a different search or filter.',
      clearFilters: 'Clear search & filters',
      clearFilter: 'Clear filter',
      emptyTagTitle: 'Nothing with this tag',
      emptyTagText: 'Try another tag, or clear the filter.',
      lovesIt: 'loves it',
      notSure: 'not sure',
      tagsFeatures: 'TAGS & FEATURES',
      addTag: 'Add a tag',
      addTagChip: 'Tag',
      removeTag: 'Remove tag',
      tagRemovedToast: 'Tag removed',
      newTagPlaceholder: 'New tag…',
      monthlyRent: 'Monthly rent',
      utilities: 'Utilities',
      deposit: 'Deposit',
      monthOne: 'month',
      monthMany: 'months',
      perMo: '/mo',
      perM2: 'PER M²',
      totalFirstMonth: 'Total first month',
      shareTitle: 'Share your shortlist',
      shareText: 'Invited people browse the same list, react and add tags — decisions get faster together.',
      close: 'Close',
      linkLabel: 'LINK',
      copy: 'Copy',
      anyoneWithLink: 'Anyone with the link',
      linkCopiedReact: 'Link copied — anyone with it can react and tag',
      linkCopiedView: 'Link copied — anyone with it can view',
      copyFailed: 'Couldn’t copy the link — please copy it manually',
      roleOwner: 'Owner',
      roleReact: 'Can react',
      roleReactSub: 'React, tag and comment',
      roleView: 'Can view',
      roleViewSub: 'Browse only',
      peopleLabel: 'PEOPLE',
      you: 'you',
      pendingInvite: "Invited — hasn't joined yet",
      removeAccess: 'Remove access',
      accessRemoved: 'Access removed',
      invite: 'Invite',
      invitePlaceholder: 'Invite by email or name…',
      inviteSent: 'Invitation sent to {name}',
      shareDemoHint: "Sharing is a preview — invites don't send yet.",
      // Public read-only sharing (replaces the collaborator flow — the
      // invite/role/reaction keys above are orphaned, kept per dict convention)
      shareToWeb: 'Share to web',
      sharingOn: 'Sharing on',
      publicChip: 'PUBLIC',
      shareWebTitle: 'Share to the web',
      shareWebAnyone: 'Anyone with the link',
      shareWebRest: ' can open your shortlist — read only. No account needed.',
      whatVisitorsSee: 'WHAT VISITORS SEE',
      visPhotos: 'Photos, prices and “what you’ll pay”',
      visTags: 'Your tags and home numbers (#1, #2…)',
      visAi: 'AI summaries',
      visCommute: 'Your commute places — kept private (they reveal where you live and work)',
      visContacts: 'Your email and contacts — never shown',
      createPublicLink: 'Create public link',
      unguessableHint: 'The link is a random code — unguessable, and hidden from search engines and scrapers. You can stop sharing anytime.',
      publicLinkLabel: 'PUBLIC LINK',
      seeVisitors: 'See what visitors see',
      stopSharing: 'Stop sharing',
      stopSharingHint: 'Stop sharing kills the link immediately. Sharing again creates a brand-new one.',
      publicBannerTitle: 'This page is public',
      publicBannerText: 'Anyone with the link can view it — read only. Commute places stay private.',
      copyLink: 'Copy link',
      visitorView: 'Visitor view',
      linkCopied: 'Link copied',
      nowPublicToast: 'Your shortlist is now public — link copied',
      nowPublicToastNoCopy: 'Your shortlist is now public',
      sharingStoppedToast: 'Sharing stopped — the link is dead',
      previewBar: 'You’re previewing the public page — exactly what anyone with the link sees.',
      exitPreview: 'Exit preview',
      viewOnly: 'VIEW ONLY',
      shortlistOf: '{name}’s shortlist',
      sharedVia: 'Shared via Roostr',
      numberTip: 'Home #{n} — stable number, easy to refer to',
    },
    notifications: {
      title: 'Notifications',
      signInMessage: 'Sign in to see alerts for new flats from your saved searches.',
      signIn: 'Sign in',
      allCaughtUp: 'All caught up',
      unread: 'unread',
      markAllRead: 'Mark all read',
      all: 'All',
      unreadTab: 'Unread',
      priceDrops: 'Price drops',
      loadError: 'Failed to load notifications',
      tryAgain: 'Try again',
      emptyTitle: 'No notifications yet',
      emptyText: 'New flats from your saved searches will appear here.',
      openListing: 'Open listing',
      openFlats: 'Open flats',
      new: 'New',
      newMatch: 'new match',
      newMatches: 'new matches',
      loadMore: 'Load more',
      loading: 'Loading...',
      justNow: 'Just now',
      agoMinutes: 'm',
      agoHours: 'h',
      agoDays: 'd',
      today: 'Today',
      yesterday: 'Yesterday',
      earlier: 'Earlier',
      // Roostr Web redesign — What's-new review queue (Phase 7.1)
      whatsNew: "What's new",
      toReview: 'to review',
      subtitle: 'Fresh matches across your saved searches. Like what you want to keep — it lands in Liked. Skip the rest.',
      allSearches: 'All searches',
      clearAll: 'Clear all',
      clearAllTip: 'Skip everything left (undoable)',
      clearedOne: '1 home skipped',
      clearedFew: '{n} homes skipped',
      clearedMany: '{n} homes skipped',
      undo: 'Undo',
      skip: 'Skip',
      skipTip: 'Drop this one',
      like: 'Like',
      likeTip: 'Send to your Liked shortlist',
      summary: 'Summary',
      summaryTip: 'AI summary of the listing description',
      summaryError: 'The summary could not be generated.',
      subtitleList: "Skim the list, save what's worth a look, mark the rest seen — or swipe through just the newest {n}.",
      newOne: 'new',
      newFew: 'new',
      newMany: 'new',
      modeAll: 'All',
      modeSwipe: 'Swipe',
      selected: 'selected',
      saveToLiked: 'Save to Liked',
      markSeen: 'Mark seen',
      cancel: 'Cancel',
      bulkHint: 'Select rows for bulk actions, or:',
      markAllSeen: 'Mark all {n} seen',
      markAllTip: 'Clears the counter — nothing is deleted',
      markDaySeen: 'Mark day seen',
      selectDay: 'Select whole day',
      deselectDay: 'Deselect day',
      select: 'Select',
      deselect: 'Deselect',
      showMore: 'Show {n} more',
      groupToday: 'Today',
      groupYesterday: 'Yesterday',
      groupWeek: 'This week',
      groupEarlier: 'Earlier',
      allSeen: 'all seen',
      emptyListTitle: 'Nothing here',
      emptyScopedA: 'No matches from',
      emptyScopedB: 'yet.',
      emptyAll: 'New matches from your saved searches will land here.',
      sessionHint: 'Only the newest {target} in this session — {n} more wait in All.',
      sessionDoneTitle: 'Newest {n} reviewed',
      sessionEmptyTitle: 'Nothing new to swipe',
      sessionDoneText: '{n} older matches remain — skim them in All, or grab another stack.',
      swipeMore: 'Swipe {n} more',
      backToAll: 'Back to All',
      utilsShort: 'utils',
      underMedian: '{n}% under median',
      overMedian: '{n}% over median',
      dealTip: "Price per m² vs this list's median",
      mustHavesMet: '{met}/{n} must-haves',
      moreTags: '+{n} tags',
      lessTags: 'less',
      openInMaps: 'Open in Google Maps',
      openPhotoTip: 'Open the full picture view (Enter)',
      stackCapHint: 'Showing the newest {n} — older matches age out.',
      floorAbbr: 'fl.',
      photoCountTip: 'Number of photos in the listing',
      incomingOne: 'Show 1 new match',
      incomingFew: 'Show {n} new matches',
      incomingMany: 'Show {n} new matches',
      seenOne: '1 marked as seen',
      seenFew: '{n} marked as seen',
      seenMany: '{n} marked as seen',
      inLiked: 'In your Liked shortlist',
      removeFromLiked: 'Remove from Liked',
      markSeenTip: 'Mark seen — clears it from the counter',
      newHomes: 'New homes',
      markAllSeenShort: 'Mark all seen',
      bellMarkAllTip: 'Clear the counter — homes stay browsable in View all',
      bellCaughtUp: 'All caught up — new matches land here.',
      viewAll: 'View all',
      bellTip: "What's new — fresh matches",
      perM2: 'PER M²',
      totalFirstMonth: 'Total first month',
      loadErrorText: 'Something went wrong while fetching your new matches.',
      caughtUpTitle: "That's everything new",
      caughtUpScopedA: 'No new homes left in',
      caughtUpScopedB: 'Check the other searches or come back later.',
      caughtUpAll: 'You are all caught up. New matches will land here as they appear.',
      openLiked: 'Open Liked',
    },
    about: {
      backToSearch: 'Back to search',
      title: 'About Roostr',
      subtitle: 'Fast, user-friendly, AI-powered apartment search.',
      whoWeAre: 'Who We Are',
      whoWeAreText: "We're two people. We work from our apartments — the ones we found the hard way, before Roostr existed.",
      whyBuilt: 'Why We Built This',
      whyBuiltText: 'We want a service that is fast, easy to use, and quick to navigate.',
      nerdyBit: 'The Nerdy Bit',
      nerdyIntro: 'This entire website was built with the help of',
      nerdyMiddle: 'and',
      nerdyOutro: 'Yes, we pair-programmed with an AI.',
      contactText: 'Want to say hi? Drop us a line at',
      contactNote: "(We read every email. There's only two of us, remember?)",
    },
    pricing: {
      backToSearch: 'Back to search',
      choosePlan: 'Choose your plan',
      subtitle: 'Roostr is free to use. Upgrade to Penthouse to unlock all features and supercharge your apartment hunt.',
      checkoutCanceled: 'Checkout was canceled. You can try again anytime.',
      freeForever: 'Free forever',
      perMonth: '/month',
      included: 'Included',
      currentPlan: 'Current plan',
      getStarted: 'Get started',
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      perThreeMonths: '/3 months',
      savePercent: 'Save',
      loading: 'Loading...',
      betaFree: 'Free',
      duringBeta: 'during beta',
      everythingPlus: 'Everything in Roostr, plus',
      redirecting: 'Redirecting...',
      upgrade: 'Upgrade',
      signInToUpgrade: 'Sign in to upgrade',
      paymentsSafe: 'Payments are processed securely by Stripe. We never store your payment information. You can cancel anytime from your account settings.',
    },
    listingCard: {
      openInMaps: 'Show on Google Maps',
      mustHaveMatch: 'Must-have — matched (edit in Settings)',
      roomOne: 'room',
      roomMany: 'rooms',
      floor: 'Floor',
      rent: 'rent',
      noPrice: 'No price',
      estTotal: 'Est. total',
      costBreakdown: 'Cost breakdown',
      utilities: 'Utilities',
      serviceCharges: 'Service charges',
      parking: 'Parking',
      internet: 'Internet',
      deposit: 'Deposit',
      monthOne: 'month',
      monthMany: 'months',
      aiNote: 'AI-analyzed. May not be accurate.',
      more: 'more',
      less: 'less',
      viewSource: 'View source',
      hideListing: 'Hide this listing',
      previousImage: 'Previous image',
      nextImage: 'Next image',
      unavailable: 'May no longer be available',
      monthlyRent: 'Monthly rent',
      perMo: '/mo',
      details: 'Details',
      openDetails: 'Open listing details',
      postedOnRoostr: 'Posted on Roostr',
      postedOnRoostrTip: 'Posted directly on Roostr by the owner — ranked above imported listings in search',
      sourceTip: 'Original listing on',
      liveUpdateTip: 'Live update from the owner — posted without changing the listing.',
      allFeatures: 'All features',
      showAllFeatures: 'Show all features',
      filterBy: 'Filter by',
      like: 'Like',
      unlike: 'Remove from liked',
      statRooms: 'ROOMS',
      statArea: 'AREA',
      statFloor: 'FLOOR',
    },
    savedSearches: {
      title: 'Searches & Agents',
      subtitle: 'Deploy personal agents to find your next apartment',
      activeOf: 'active',
      loading: 'Loading your searches...',
      tryAgain: 'Try Again',
      emptyTitle: 'No saved searches yet',
      emptyText: 'Save your search criteria to quickly access them later and deploy agents to monitor for new listings.',
      saved: 'Saved',
      showAll: 'Show All',
      new: 'New',
      startNotifications: 'Turn on notifications',
      stopNotifications: 'Turn off notifications',
      found7d: 'found · 7d',
      failedToLoad: 'Failed to load saved searches',
      failedToDelete: 'Failed to delete search',
      failedToUpdate: 'Failed to update notification',
      signInToView: 'Please sign in to view saved searches',
      allDeployed: 'All 5 agents are deployed! Recall one to send another on a new mission.',
    },
    listingDetail: {
      rateLimitExceeded: 'Rate limit exceeded',
      notFound: 'Listing not found',
      rateLimitHint: "You've made too many requests. Please wait a moment and try again, or sign in for higher limits.",
      notFoundHint: 'This listing may have been removed or the link is incorrect.',
      backToSearch: 'Back to search',
      copied: 'Copied!',
      share: 'Share',
      viewOnMarketplace: 'View on',
      originalSite: 'original site',
      view: 'View',
      goToImage: 'Go to image',
      unavailableHint: "Photos couldn't be loaded from the original source. We'll look into it.",
      visitOriginal: 'Visit original',
      additionalCostsApply: 'Additional costs apply',
      baseRent: 'Base rent',
      utilitiesEstimate: 'Utilities estimate',
      internetTv: 'Internet / TV',
      depositRequired: 'Deposit required',
      aiCostHint: 'AI-analyzed from listing description. Actual costs may vary.',
      featuresAmenities: 'Features & Amenities',
      featuresHint: 'Automatically extracted by AI from listing text and photos',
      fromDescription: 'From listing description:',
      detectedFromPhotos: 'Detected from photos:',
      featuresAccuracyHint: 'AI-extracted features may not be accurate.',
      description: 'Description',
      translatedTo: 'Translated to',
      justFacts: 'Just the Facts',
      showFullDescription: 'Show full description',
      showOriginal: 'Show original',
      translate: 'Translate',
      chooseLanguage: 'Choose language',
      translateTo: 'Translate to',
      justFactsTitle: 'Summarize to key property details only',
      translatingTo: 'Translating to',
      summarizing: 'Summarizing...',
      location: 'Location',
      openInGoogleMaps: 'Open in Google Maps',
      listedOn: 'Listed on',
      lastUpdated: 'Last updated',
      // Roostr Web redesign — detail page (Phase 5.2)
      back: 'Back',
      summarizeAi: 'Summarize with AI',
      summarizeAiTitle: 'Generate an AI summary of the listing description',
      aiSummary: 'AI summary',
      aiSummaryDisclaimer: 'AI-generated from the listing text — may not be accurate.',
      hideSummary: 'Hide',
      features: 'Features',
      commuteFromHere: 'Commute from here',
      marketPosition: 'Market position',
      whatYoullPay: "What you'll pay",
      perMo: '/mo',
      perM2: 'per m²',
      monthlyRent: 'Monthly rent',
      utilities: 'Utilities',
      deposit: 'Deposit',
      depositMonths: 'Deposit ({n} months)',
      totalFirstMonth: 'Total first month',
      openOn: 'Open on',
      addToLiked: 'Add to Liked',
      inLiked: 'In your Liked shortlist',
      removeFromLiked: 'Remove from Liked',
      mustHaves: 'Must-haves',
      listedMeta: 'Listed {when} on {marketplace}',
      listedWhenOnly: 'Listed {when}',
      energyClass: 'Energy class',
      agoToday: 'today',
      agoOneDay: '1d ago',
      agoDays: '{n}d ago',
      mapPreview: 'map preview',
    },
    searches: {
      title: 'Saved searches',
      subtitle: "Each search watches the market for you and files new matches into What's new.",
      alerts: 'Alerts',
      alertsOff: 'Off',
      alertsDaily: 'Daily',
      alertsInstant: 'Instant',
      alertsToastDaily: 'One digest every morning',
      alertsToastInstant: 'Alerts the moment a match lands',
      reviewAll: 'Review all {n} new',
      newOne: '1 new',
      newFew: '{n} new',
      newMany: '{n} new',
      savedOn: 'Saved {date}',
      pausedHint: 'Alerts paused',
      openTitle: 'Open this search',
      reviewTitle: 'Review the new matches',
      menuTitle: 'Search options',
      menuOpenInSearch: 'Open in search',
      menuPause: 'Pause alerts',
      menuResume: 'Resume alerts',
      menuDelete: 'Delete search',
      deletedToast: 'Search deleted',
      deleteFailed: 'Failed to delete the search',
      restoreFailed: 'Could not restore the search',
      pausedToast: 'Alerts paused',
      resumedToast: 'Alerts resumed',
      updateFailed: 'Failed to save the change',
      emptyTitle: 'No saved searches yet',
      emptyText: 'Filter the listings the way you like, then hit "Save search" — we\'ll watch the market for you.',
      startSearching: 'Start searching',
      signInTitle: 'Sign in to keep searches',
      signInText: 'Saved searches watch the market and alert you the moment new matches appear.',
      signIn: 'Sign in',
      back: 'Back',
      notFoundTitle: 'Search not found',
      notFoundText: 'It may have been deleted.',
      reviewOne: 'Review 1 new match',
      reviewFew: 'Review {n} new matches',
      reviewMany: 'Review {n} new matches',
      reviewSub: 'Fresh since you last looked — like or skip each one.',
      matchesOne: 'match',
      matchesFew: 'matches',
      matchesMany: 'matches',
      sortNewest: 'Newest',
      sortPriceAsc: 'Price ↑',
      sortPriceDesc: 'Price ↓',
      sortSize: 'Size',
      newBadge: 'NEW',
      perMonth: '/mo',
      noMatchesTitle: 'No matches yet',
      noMatchesText: 'As soon as listings match this search, they will show up here.',
    },
    commute: {
      unnamed: 'Unnamed',
      openDirections: 'open directions in Google Maps',
      editPlaces: 'Edit navigation targets',
      placesTitle: 'Navigation targets',
      placesText: 'Every liked home shows the real distance and travel time to these places.',
      labelPlaceholder: 'Label',
      addressPlaceholder: 'Address',
      removePlace: 'Remove place',
      addPlace: 'Add target',
      maxPlaces: 'Up to 3 targets',
      done: 'Done',
      updateNote: 'Distances update everywhere immediately — on Liked cards and listing details.',
      straightLineTip: 'straight-line estimate (no route available)',
      straightLineNote: 'straight-line estimate where no route was available',
      noListingLocation: 'this listing has no location',
      addressNotFound: "couldn't find this address",
    },
    post: {
      title: 'Post a listing',
      subtitle: 'Six short steps. Everything you type is formatted for you — watch the preview.',
      forRent: 'For rent',
      forSale: 'For sale',
      s1Title: 'Photos',
      s1Hint: 'The one thing only you can do. Everything below gets easier after this.',
      photosDrop: 'Drop photos here, or browse',
      photosDropHint: 'Any order, any quality — they get sorted, brightened and de-duplicated for you',
      photosFootnote: 'The first photo is the cover — drag to reorder, × to remove, click a name to rename.',
      isCover: 'Cover photo — the first position is the cover',
      dragReorder: 'Drag to reorder — drop into first position to make it the cover',
      removePhoto: 'Remove photo',
      roomNamePlaceholder: 'Room…',
      roomNameLabel: 'Photo name',
      roomNameTitle: 'Click to rename this photo',
      roostrBoost: 'Listings posted on Roostr rank above imported ones — seekers see yours first.',
      uploading: 'uploading…',
      scanning: 'Recognizing rooms, checking light and sharpness…',
      fixDarkPrefix: 'Bedroom shot was dark —',
      fixDarkOn: 'brightened it',
      fixDarkOff: 'left as shot',
      fixDupPrefix: 'Two near-identical living rooms —',
      fixDupOn: 'kept the sharper one',
      fixDupOff: 'keeping both',
      enhance: 'Enhance',
      keepBoth: 'Keep both',
      undo: 'Undo',
      s2Title: 'The basics',
      s2Hint: 'Quick taps — under ten seconds. Features spotted in your photos get pre-ticked.',
      layout: 'Layout',
      area: 'Area',
      floor: 'Floor',
      elevator: 'Elevator',
      yes: 'Yes',
      no: 'No',
      spottedInPhotos: 'spotted in photos',
      featuresLabel: 'Features — tap to toggle',
      moreTags: 'All tags',
      tagPickerTitle: 'All tags',
      tagPickerHint: 'Everything seekers can filter by — tap to add to your listing.',
      tagPickerSearch: 'Search tags…',
      tagPickerEmpty: 'No tags match that search.',
      tagPickerSelected: 'selected',
      tagPickerDone: 'Done',
      tagFallbackGroup: 'Common tags',
      adviceDarkLead: 'Bedroom shot is dark.',
      adviceDarkBody: 'Consider re-shooting by day or with every light on — bright photos get noticeably more views.',
      areaTypeIt: 'type it in',
      previewLabelCard: 'YOUR CARD IN SEARCH RESULTS',
      moreTagsTitle: 'Show the full tag list',
      s3Title: 'Location',
      s3Hint: 'The full address goes on the listing, with a pin on the map.',
      addressPlaceholder: 'Street and number…',
      verified: 'Verified',
      change: 'Change',
      pulledFromMap: 'pulled from the map',
      highlightsLabel: 'Location highlights — tap to drop any',
      s4Title: 'Price',
      s4HintAnchored: 'Anchored to',
      s4HintComparables: 'comparables',
      s4HintBand: 'The green band is fair — the rest is how fast you want it gone.',
      monthlyRent: 'Monthly rent',
      perMonth: '/mo',
      expect: 'Expect',
      inquiries: 'inquiries',
      aWeek: 'a week.',
      onTopOfRent: 'On top of rent',
      utilities: 'Utilities',
      perMonthLabel: '/ month',
      deposit: 'Deposit',
      oneTime: '/ one-time',
      autoTwiceRent: 'auto 2× rent',
      autoRevertTitle: 'Back to automatic 2× rent',
      auto: 'auto',
      customFeePlaceholder: 'What’s it for?',
      remove: 'Remove',
      extraParking: 'Parking',
      extraInternet: 'Internet',
      extraCleaning: 'Cleaning',
      extraCustom: 'Custom…',
      addFee: 'Add',
      addCustomFee: 'Add your own fee',
      tenantPays: 'Tenant pays',
      allInSuffix: '/mo all-in, plus',
      depositOnce: 'deposit once.',
      s5Title: 'The story',
      s5Hint: 'Write anything — typos, caps lock, bullet points. The preview always shows the tidied version.',
      titleLabel: 'Title',
      useSuggestedTitle: 'Use the suggested title',
      use: 'Use',
      descriptionLabel: 'Description',
      descriptionPlaceholder: '3–5 sentences is the sweet spot. What do you love about living here? Skip contact info — that lives in the contact step.',
      rephrase: 'Rephrase:',
      toneWarm: 'Warm',
      toneFactual: 'Factual',
      tonePunchy: 'Punchy',
      rewriteVoice: 'Rewrite in a',
      draftVoice: 'Draft it for me —',
      voice: 'voice',
      toneEmptyHint: '— or let one of these write the first draft',
      pasteDemo: 'Paste my old ad (demo)',
      tidiedPrefix: 'Tidied on the fly — removed',
      tidiedSuffix: 'The preview shows the clean version.',
      applyHereToo: 'Apply here too',
      s6Title: 'Contact details',
      s6Hint: 'How people reach you — shown right on the listing.',
      email: 'Email',
      fromYourAccount: 'from your account',
      phone: 'Phone',
      telegram: 'Telegram',
      instagram: 'Instagram',
      scheduleLabel: 'Show contacts only at certain times',
      always: 'Always',
      scheduled: 'Scheduled',
      from: 'From',
      visibleFrom: 'Visible from',
      to: 'to',
      visibleUntil: 'Visible until',
      scheduleFootnote: 'Outside these hours your contacts are hidden from the listing.',
      publish: 'Publish listing',
      readyNote: 'Ready — you can pause or edit any time from My listings.',
      stillNeeds: 'Still needs:',
      missPhotos: 'photos',
      missLayout: 'layout',
      missArea: 'area',
      missFloor: 'floor',
      missAddress: 'address',
      missStory: 'a few sentences of story',
      missContact: 'a way to reach you',
      previewFootnote: 'Reformats live as you type — this is exactly what seekers see',
      details: 'Details',
      preview: 'Preview',
      previewHeader: 'WHAT SEEKERS WILL SEE',
      previewEnhanced: '1 photo enhanced',
      previewPhotosPlaceholder: 'YOUR PHOTOS LAND HERE',
      postedOnRoostr: 'Posted on Roostr',
      previewLabelTitle: 'TITLE — WRITTEN AT THE STORY STEP',
      previewLabelLocation: 'LOCATION',
      previewLabelPrice: 'PRICE — SET WITH REAL COMPARABLES',
      previewUtilities: 'utilities',
      previewDeposit: 'deposit',
      previewLabelFeatures: 'FEATURES — READ FROM PHOTOS & YOUR TEXT',
      previewLabelStory: 'STORY — SHOWN ON THE DETAIL PAGE',
      previewLabelContact: 'CONTACT',
      previewContactsVisible: 'Contacts visible',
      publishedTitle: 'You’re live!',
      publishedBody: 'Your listing is in front of 1,842 people searching Vinohrady right now. First saves usually land within hours.',
      openMyListings: 'Open My listings',
      postAnother: 'Post another',
    },
    myListings: {
      title: 'My listings',
      subtitleLive: 'live',
      savesBySeekers: 'saves by seekers',
      postListing: 'Post a listing',
      assistantTipsSingular: 'thing the assistant would change — each takes one tap.',
      assistantTipsPlural: 'things the assistant would change — each takes one tap.',
      emptyTitle: 'Nothing here yet',
      emptyText: 'Post your first place — the assistant does most of the typing.',
      statusLive: 'Live',
      statusPaused: 'Paused',
      statusDraft: 'Draft',
      statusRented: 'Rented',
      newBadge: 'NEW',
      actions: 'Listing actions',
      askingPerMonth: 'asking / month',
      askingPrice: 'asking price',
      completeness: 'Listing completeness',
      edit: 'Edit listing',
      editSub: 'Reopens the composer with everything filled in',
      pause: 'Pause listing',
      pauseSub: 'Hidden from search, stats kept',
      pausedToast: 'Listing paused',
      resume: 'Resume listing',
      resumedToast: 'Listing resumed — back in search results',
      backInSearch: 'Back in search results',
      markRented: 'Mark as rented',
      rentedToast: 'Congrats! 🎉 Marked as rented',
      relist: 'Relist',
      relistedToast: 'Relisted',
      delete: 'Delete',
      deletedToast: 'Listing deleted',
      dismiss: 'Dismiss',
      priceUpdatedTo: 'Price updated to',
      shareCopied: 'Share link copied',
      liveUpdates: 'LIVE UPDATES',
      liveUpdate: 'LIVE UPDATE',
      liveUpdateHint: 'one short note pinned on the listing',
      oneUpdateNote: 'One update at a time — remove this one to post another.',
      promoTitle: 'Promote this listing',
      promoManage: 'Manage promotion',
      promoSubtitle: 'Five boosts for one week, one payment. Stops automatically — no auto-renew. This demo charges nothing.',
      promoBadge: 'PROMOTED',
      promoActive: 'ACTIVE',
      promoActivate: 'Promote for 1 week',
      promoWeek: 'week',
      promoDemo: 'demo, no charge',
      promoEnd: 'End early',
      promoEndsIn: 'Ends in',
      promoDaysLong: 'days',
      promoAutoStop: 'stops automatically, no auto-renew',
      promoActivatedToast: 'Promoted for 7 days — stops automatically, no auto-renew (demo, no charge)',
      promoEndedToast: 'Promotion ended',
      promoDeckTitle: 'Front of the swipe deck',
      promoDeckDesc: 'A guaranteed slot among the first cards of the daily deck for every seeker whose search matches.',
      promoDeckActive: 'Live — showing in first 3 cards of matching decks',
      promoStripTitle: 'Highlighted dot on the price strip',
      promoStripDesc: 'Your listing stands out inside the search page’s price chart — coral, ringed, mini-card ready.',
      promoStripActive: 'Live on every matching search',
      promoBumpTitle: 'Freshness bump — every 3 days',
      promoBumpDesc: 'One tap re-sorts your listing like it was posted today. No deleting, no reposting, saves stay.',
      promoBumpOld: '24 days ago',
      promoBumpNew: 'JUST NOW',
      promoBumpNow: 'Bump now',
      promoBumpIn: 'Next bump in',
      promoBumpDays: 'd',
      promoBumpedToast: 'Freshness bumped — your listing sorts like new again',
      promoAbTitle: 'A/B cover & title testing',
      promoAbDesc: 'We rotate your cover photo and title automatically, measure clicks and lock in the winner.',
      promoAbActive: 'Testing — variant B leads (6.3% vs 4.1% CTR)',
      promoSocialTitle: 'Social proof on the card',
      promoSocialDesc: 'Real demand numbers on your card — scarcity moves renters faster than any badge. Opt-in, never invented.',
      promoSocialActive: 'Live —',
      promoSocialShort: 'shortlisted',
      promoSocialViewings: 'viewing requests',
      editPrice: 'Change the asking price',
      editPrices: 'Edit rent, utilities, deposit and fees',
      pricesLabel: 'PRICES',
      pricesHint: 'changes go live immediately',
      viewTitle: 'How seekers see it',
      viewInSearch: 'In search',
      viewDetail: 'Detail page',
      liveUpdatesHint: 'pinned on the listing, description stays untouched',
      removeUpdate: 'Remove update',
      updatePlaceholder: 'e.g. Viewings this Saturday · Available from Sept 1 …',
      updatePosted: 'Live update posted — pinned to the top of your listing',
      post: 'Post',
    },
  },
}

// Flatten a nested Messages tree into a dot-joined key → string map
// (e.g. { search: { grid: 'Grid' } } → { 'search.grid': 'Grid' }).
// Only string leaves are kept, so a lookup misses (undefined) exactly where
// the old path.split('.') + reduce walk returned undefined: missing keys,
// partial paths (landing on a nested object), or paths through a string.
function flattenMessages(obj: Messages, prefix = ''): Record<string, string> {
  const out: Record<string, string> = {}
  for (const [key, value] of Object.entries(obj)) {
    const path = prefix ? `${prefix}.${key}` : key
    if (typeof value === 'string') {
      out[path] = value
    } else {
      Object.assign(out, flattenMessages(value, path))
    }
  }
  return out
}

// Derived once at module init from the nested `messages` source above —
// the nested object stays the single source of truth for the dictionary.
// t() then costs two O(1) hash lookups instead of re-walking the tree
// (split + reduce, twice on a cs-miss) on every call; the redesigned cards
// call t() dozens of times per render.
const flatMessages: Record<UiLanguage, Record<string, string>> = {
  cs: flattenMessages(messages.cs),
  en: flattenMessages(messages.en),
}

export function useI18n() {
  const { uiLanguage, setUiLanguage } = useUIStore()

  const t = React.useCallback((key: string, fallback?: string) => {
    return flatMessages[uiLanguage][key]
      ?? flatMessages.en[key]
      ?? fallback
      ?? key
  }, [uiLanguage])

  return {
    uiLanguage,
    setUiLanguage,
    t,
  }
}
